import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from '../Toast';
import { askConfirm } from '../ui';
import { STATUS_LABELS, LOAN_STATUS_LABELS, SERVICE_TYPE_LABELS, SERVICE_TYPE_COLORS, crmApi, operatorsApi, authAxios, CrmOperator } from '../../services/crmApi';

// ══════ LISTA ZAKUPÓW KLIENTA (osobny fetch, nie cache) ══════
function ClientPurchasesSection({ applicationId }: { applicationId: number }) {
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    authAxios.get(`/api/crm/applications/${applicationId}/purchases`)
      .then(r => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setPurchases(r.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [applicationId]);

  if (loading) return <div className="bg-orange-50 rounded-xl p-4 text-center text-orange-600 animate-pulse">Wczytywanie zakupów klienta...</div>;
  if (purchases.length === 0) return null;

  const total = purchases.reduce((s: number, p: any) => s + (Number(p.total_price) || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-orange-200 overflow-hidden">
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center text-base">
          <span className="mr-2 text-xl">🛒</span>
          LISTA ZAKUPÓW KLIENTA
        </h3>
        <div className="bg-white/20 px-3 py-1 rounded-full">
          <span className="text-white text-sm font-bold">
            {purchases.length} poz. — {total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł
          </span>
        </div>
      </div>
      <div className="max-h-[500px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700 w-8">#</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Nazwa produktu</th>
              <th className="text-right px-3 py-2 font-semibold text-gray-700 w-28">Kwota</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700 w-16">Link</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p: any, i: number) => (
              <tr key={p.id || i} className={`border-t border-gray-100 hover:bg-orange-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                <td className="px-3 py-2 text-gray-400 font-mono text-xs">{i + 1}</td>
                <td className="px-3 py-2 text-gray-800 font-medium" title={p.item_name}>{p.item_name}</td>
                <td className="px-3 py-2 text-right text-gray-900 font-semibold whitespace-nowrap">{Number(p.total_price || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
                <td className="px-3 py-2 text-center">{p.purchase_link ? <a href={p.purchase_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-7 h-7 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors">🔗</a> : <span className="text-gray-300">—</span>}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-orange-50 border-t-2 border-orange-200">
            <tr>
              <td colSpan={2} className="px-3 py-3 font-bold text-gray-700 text-base">RAZEM</td>
              <td className="px-3 py-3 text-right font-bold text-orange-700 text-lg">{total.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} zł</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
import { Edit, Save, X, Sparkles, MessageSquare, FileText, Download, Calculator, Mail, Folder, FileOutput, FileType, Send, TrendingUp, ShoppingCart, Award, BookOpen, FileSpreadsheet, Link as LinkIcon, ExternalLink, FolderOpen, Copy, Phone, Banknote, ClipboardCheck, CalendarClock, Building2, FileCheck, Clock, CheckCircle, XCircle, Scan, Loader2, UserCircle, Globe, ChevronUp, ChevronDown, BarChart3 } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { StatusTimeline } from '../StatusTimeline';
import { TagSelector } from '../tags/TagSelector';
import { IndustryIntelligencePanel } from '../IndustryIntelligencePanel';
import { ContractAcceptanceSection } from '../ContractAcceptanceSection';
import { CommunicationHistoryPanel } from '../CommunicationHistoryPanel';
import { CommunicationTimeline } from './CommunicationTimeline';
import CommentsSection from './CommentsSection';
import TasksSection from './TasksSection';
import UnifiedTimeline from './UnifiedTimeline';
import { ClientChat } from './ClientChat';
import { PortalChatPanel } from './PortalChatPanel';
import { PortalMeetingsPanel } from './PortalMeetingsPanel';
import { GmailCorrespondence } from './GmailCorrespondence';
import { NextBestActionWidget } from './NextBestActionWidget';
import { FollowUpWidget } from './FollowUpWidget';
import { MentionsInput } from './MentionsInput';
import { isLoanProgram, isPupProgram, getProgramByServiceType, getTerminology } from '../../config/programs';
import { useProductScoring } from '../../hooks/useProductScoring';
import { ProductRoadmap } from './ProductRoadmap';
import { DocumentChecklistWidget } from './DocumentChecklistWidget';
import { InvoicesSection } from './InvoicesSection';
import { GuarantorsSection } from './GuarantorsSection';
import { PupCouponButton } from './PupCouponButton';
import { CompletedCoursesPanel } from './CompletedCoursesPanel';
import { LoanCalculatorWidget } from './LoanCalculatorWidget';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface Props { h: ReturnType<typeof useApplicationDetail>; }

// Per-product AI quick actions (non-PUP products)
const PRODUCT_AI_ACTIONS: Record<string, Array<{ type: string; label: string; tooltip: string; icon: string; gradient: string }>> = {
  wycieczki: [
    { type: 'route_suggestion', label: 'AI Sugestia trasy', tooltip: 'AI zaproponuje optymalną trasę na podstawie profilu grupy.', icon: 'Globe', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'group_pricing', label: 'Wycena grupy', tooltip: 'Automatyczna kalkulacja kosztów grupy wycieczkowej.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600' },
    { type: 'logistics_plan', label: 'Plan logistyczny', tooltip: 'AI wygeneruje plan logistyczny wycieczki (transport, posiłki, noclegi).', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-indigo-600 to-violet-600' },
    { type: 'safety_checklist', label: 'Checklist bezpieczeństwa', tooltip: 'Lista wymagań bezpieczeństwa dla grupy.', icon: 'FileText', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
  ],
  turnusy: [
    { type: 'pfron_matching', label: 'Matching PFRON', tooltip: 'AI oceni kwalifikowalność PFRON i dopasuje ośrodek.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'document_checklist', label: 'Checklist dokumentów', tooltip: 'Lista wymaganych dokumentów PFRON dla klienta.', icon: 'FileText', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'group_pricing', label: 'Wycena grupy', tooltip: 'Kalkulacja kosztów turnusu (PFRON + dopłata).', icon: 'Calculator', gradient: 'bg-gradient-to-r from-cyan-600 to-blue-600' },
    { type: 'logistics_plan', label: 'Plan logistyczny', tooltip: 'AI wygeneruje plan transportu i zakwaterowania.', icon: 'Globe', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
  ],
  zus: [
    { type: 'zus_letter_draft', label: 'Draft pisma ZUS', tooltip: 'AI wygeneruje projekt pisma do ZUS.', icon: 'FileText', gradient: 'bg-gradient-to-r from-red-600 to-rose-600' },
    { type: 'debt_analysis', label: 'Analiza zadłużenia', tooltip: 'Ocena możliwości ratalnych lub umorzenia.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'repayment_schedule', label: 'Harmonogram spłat ZUS', tooltip: 'AI przygotuje propozycję harmonogramu spłat.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600' },
    { type: 'appeal_draft', label: 'Odwołanie AI', tooltip: 'AI wygeneruje projekt odwołania od decyzji ZUS.', icon: 'FileText', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
  ],
  bhp: [
    { type: 'training_needs', label: 'Potrzeby szkoleniowe', tooltip: 'AI oceni jakie szkolenia BHP potrzebuje firma.', icon: 'BookOpen', gradient: 'bg-gradient-to-r from-green-600 to-emerald-600' },
    { type: 'cert_verification', label: 'Weryfikacja certyfikatów', tooltip: 'Sprawdźenie ważności certyfikatów BHP pracowników.', icon: 'Award', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'training_program', label: 'Program szkolenia', tooltip: 'AI wygeneruje program szkolenia BHP dopasowany do branży.', icon: 'FileText', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'risk_assessment', label: 'Ocena ryzyka', tooltip: 'AI oceni ryzyko zawodowe na stanowiskach pracy.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-red-600 to-rose-600' },
  ],
  hutnik_biznes: [
    { type: 'partner_proposal', label: 'Propozycja partnerska AI', tooltip: 'AI wygeneruje propozycję współpracy/sponsoringu.', icon: 'FileText', gradient: 'bg-gradient-to-r from-amber-600 to-yellow-600' },
    { type: 'partner_scoring', label: 'Scoring partnera', tooltip: 'Ocena potencjału partnerskiego (brand fit, budżet, lokalność).', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
    { type: 'pricing_proposal', label: 'Propozycja cenowa', tooltip: 'AI wygeneruje ofertę cenową pakietu sponsorskiego.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'competitor_analysis', label: 'Analiza konkurencji', tooltip: 'AI przeanalizuje sponsoring konkurencji w okolicy.', icon: 'Globe', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
  ],
  marketing_beauty: [
    { type: 'marketing_brief', label: 'Brief AI', tooltip: 'AI wygeneruje brief marketingowy na podstawie danych klienta.', icon: 'FileText', gradient: 'bg-gradient-to-r from-pink-600 to-rose-600' },
    { type: 'marketing_strategy', label: 'Strategia marketingowa', tooltip: 'Propozycja strategii dla branży beauty.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-fuchsia-600 to-pink-600' },
    { type: 'pricing_proposal', label: 'Propozycja cenowa', tooltip: 'AI wygeneruje ofertę cenową usług marketingowych.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'competitor_analysis', label: 'Analiza konkurencji', tooltip: 'AI przeanalizuje konkurencję w branży beauty w okolicy.', icon: 'Globe', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
  ],
  gastro_consulting: [
    { type: 'gastro_brief', label: 'Brief AI', tooltip: 'AI wygeneruje brief konsultingowy dla lokalu gastronomicznego.', icon: 'FileText', gradient: 'bg-gradient-to-r from-orange-600 to-red-600' },
    { type: 'operations_audit', label: 'Audyt operacyjny', tooltip: 'AI oceni kluczowe obszary operacyjne lokalu.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'pricing_proposal', label: 'Propozycja cenowa', tooltip: 'AI wygeneruje ofertę cenową usług konsultingowych.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'menu_optimization', label: 'Optymalizacja menu', tooltip: 'AI zaproponuje optymalizację menu i food costu.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
  ],
  czyste_powietrze: [
    { type: 'subsidy_calculator', label: 'Kalkulator dotacji', tooltip: 'Szacunkowa kwota dofinansowania Czyste Powietrze.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-green-600 to-emerald-600' },
    { type: 'audit_checklist', label: 'Checklist audyt', tooltip: 'Lista wymagań audytu energetycznego.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-teal-600 to-cyan-600' },
    { type: 'cost_justification', label: 'Uzasadnienie kosztów', tooltip: 'AI wygeneruje uzasadnienie wydatków termomodernizacyjnych.', icon: 'FileText', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'contractor_requirements', label: 'Wymagania wykonawcy', tooltip: 'AI przygotuje specyfikację wymagań dla wykonawcy.', icon: 'FileText', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
  ],
  kfs: [
    { type: 'training_plan', label: 'Plan szkolenia AI', tooltip: 'AI zaproponuje optymalny plan szkoleniowy KFS.', icon: 'BookOpen', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'kfs_justification', label: 'Uzasadnienie AI', tooltip: 'AI wygeneruje uzasadnienie wniosku KFS.', icon: 'FileText', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
    { type: 'training_matching', label: 'Dopasowanie szkoleń', tooltip: 'AI dopasuje szkolenia do potrzeb pracowników.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'effectiveness_report', label: 'Raport efektywności', tooltip: 'AI prognozuje efektywność szkolenia i ROI.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
  ],
  szkolenia: [
    { type: 'training_plan', label: 'Plan szkolenia AI', tooltip: 'AI zaproponuje program szkolenia.', icon: 'BookOpen', gradient: 'bg-gradient-to-r from-indigo-600 to-violet-600' },
    { type: 'training_matching', label: 'Dopasowanie kursu', tooltip: 'AI dopasuje kurs z katalogu do profilu klienta.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'effectiveness_report', label: 'Prognoza efektywności', tooltip: 'AI oceni oczekiwane efekty szkolenia.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'certificate_draft', label: 'Szablon certyfikatu', tooltip: 'AI przygotuje treść certyfikatu ukończenia.', icon: 'Award', gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600' },
  ],
  granty_fpn: [
    { type: 'project_card', label: 'Karta projektu AI', tooltip: 'AI wygeneruje kartę projektu grantowego.', icon: 'FileText', gradient: 'bg-gradient-to-r from-emerald-600 to-green-600' },
    { type: 'feasibility_analysis', label: 'Analiza wykonalności', tooltip: 'Ocena wykonalności i innowacyjności projektu.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600' },
    { type: 'budget_optimization', label: 'Optymalizacja budżetu', tooltip: 'AI zoptymalizuje budżet projektu grantowego.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'justification_generator', label: 'Generator uzasadnień', tooltip: 'AI wygeneruje uzasadnienia wydatków w projekcie.', icon: 'FileText', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
  ],
  uslugi_ksiegowe: [
    { type: 'package_match', label: 'Dopasuj pakiet', tooltip: 'AI dobierze optymalny pakiet księgowy dla firmy.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'cost_analysis', label: 'Analiza kosztów', tooltip: 'Szacunkowe koszty obsługi księgowej.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'tax_optimization', label: 'Optymalizacja podatkowa', tooltip: 'AI zaproponuje optymalizację formy opodatkowania.', icon: 'FileText', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
    { type: 'compliance_check', label: 'Compliance check', tooltip: 'AI sprawdźi zgodność z aktualnymi przepisami.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
  ],
  dotacje_rozwojowe: [
    { type: 'eligibility_check', label: 'Kwalifikowalność AI', tooltip: 'AI sprawdźi kwalifikowalność do programów dotacyjnych.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-green-600 to-emerald-600' },
    { type: 'business_plan_ai', label: 'Biznesplan AI', tooltip: 'AI wygeneruje zarys biznesplanu pod dotację.', icon: 'FileText', gradient: 'bg-gradient-to-r from-violet-600 to-purple-600' },
    { type: 'budget_optimization', label: 'Optymalizacja budżetu', tooltip: 'AI zoptymalizuje budżet projektu dotacyjnego.', icon: 'Calculator', gradient: 'bg-gradient-to-r from-blue-600 to-cyan-600' },
    { type: 'justification_generator', label: 'Generator uzasadnień', tooltip: 'AI wygeneruje uzasadnienia wydatków w projekcie.', icon: 'FileText', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
    { type: 'purchase_list', label: 'Lista zakupów', tooltip: 'AI wygeneruje listę zakupów z ofertami.', icon: 'FileText', gradient: 'bg-gradient-to-r from-teal-600 to-emerald-600' },
  ],
  steam_education: [
    { type: 'steam_curriculum', label: 'Program STEAM AI', tooltip: 'AI zaproponuje program STEAM dopasowany do szkoły.', icon: 'BookOpen', gradient: 'bg-gradient-to-r from-purple-600 to-violet-600' },
    { type: 'steam_school_report', label: 'Raport szkoły', tooltip: 'AI wygeneruje raport gotowości szkoły do programu STEAM.', icon: 'FileText', gradient: 'bg-gradient-to-r from-blue-600 to-indigo-600' },
    { type: 'teacher_training_plan', label: 'Plan szkoleń nauczycieli', tooltip: 'AI przygotuje plan szkolenia kadry pedagogicznej.', icon: 'ClipboardCheck', gradient: 'bg-gradient-to-r from-emerald-600 to-teal-600' },
    { type: 'progress_report', label: 'Raport postępów', tooltip: 'AI wygeneruje raport z postępów uczniów i szkoły.', icon: 'TrendingUp', gradient: 'bg-gradient-to-r from-amber-600 to-orange-600' },
  ],
};

// Unified welcome email labels per product (replaces 7x duplicated buttons)
const WELCOME_EMAIL_LABELS: Record<string, string> = {
  wycieczki: 'Wyślij potwierdźenie rezerwacji',
  turnusy: 'Wyślij potwierdźenie rezerwacji',
  hutnik_biznes: 'Wyślij propozycję współpracy',
  marketing_beauty: 'Wyślij brief do klienta',
  gastro_consulting: 'Wyślij brief do klienta',
  granty_fpn: 'Wyślij informację o naborze',
  dotacje_rozwojowe: 'Wyślij info o naborach',
  kfs: 'Wyślij ofertę szkoleniową',
  czyste_powietrze: 'Wyślij potwierdźenie zgłoszenia',
  steam_education: 'Wyślij info o programie STEAM',
  szkolenia: 'Wyślij ofertę szkoleniową',
};

// Unified document package labels
const DOC_PACKAGE_LABELS: Record<string, string> = {
  marketing_beauty: 'Wyślij ofertę/wycenę',
  gastro_consulting: 'Wyślij ofertę/wycenę',
  granty_fpn: 'Wyślij checklist dokumentów',
  czyste_powietrze: 'Wyślij checklist dokumentów',
  dotacje_rozwojowe: 'Wyślij pakiet dokumentów',
};

// External admin panel URLs (products with separate landing admin)
const ADMIN_PANEL_URLS: Record<string, string> = {
  wycieczki: 'https://wycieczki.fundacjanh.org/admin',
  turnusy: 'https://turnusy.fundacjanh.org/admin',
  hutnik_biznes: 'https://hutnik.krakow.business/admin',
};

const SMS_PUP_TEMPLATES = [
  { label: 'Przypomnienie o dokumentach', text: (name: string) => `${name}, przypominamy o dostarczeniu brakujących dokumentów do wniosku o dotację. Prosimy o kontakt: 575 661 551 lub 534 661 551. Fundacja NH` },
  { label: 'Zaproszenie na spotkanie', text: (name: string) => `${name}, zapraszamy na spotkanie w sprawie wniosku o dotację. Prosimy o kontakt w celu ustalenia terminu: 575 661 551 lub 534 661 551. Fundacja NH` },
  { label: 'Potwierdźenie przyjęcia wniosku', text: (name: string) => `${name}, potwierdźamy przyjęcie Twojego wniosku o dotację. Skontaktujemy się wkrótce z dalszymi informacjami. Fundacja NH` },
  { label: 'Prośba o kontakt', text: (name: string) => `${name}, prosimy o kontakt z biurem Fundacji NH w sprawie wniosku o dotację: 575 661 551, 534 661 551 lub dotacje@fundacjanh.org` },
];

const SMS_LOAN_TEMPLATES = [
  { label: 'Przypomnienie o dokumentach', text: (name: string) => `${name}, przypominamy o dostarczeniu brakujących dokumentów do wniosku o pożyczkę. Prosimy o kontakt: 575 661 551 lub 534 661 551. Fundacja NH` },
  { label: 'Status wniosku pożyczkowego', text: (name: string) => `${name}, Twój wniosek o pożyczkę jest w trakcie rozpatrywania. Skontaktujemy się z informacją o decyzji. Fundacja NH: 575 661 551` },
  { label: 'Kompletacja biznesplanu', text: (name: string) => `${name}, prosimy o dostarczenie uzupełnionego biznesplanu do wniosku o pożyczkę. Kontakt: 575 661 551 lub 534 661 551. Fundacja NH` },
  { label: 'Prośba o kontakt', text: (name: string) => `${name}, prosimy o kontakt z biurem Fundacji NH w sprawie pożyczki Wsparcie na Starcie: 575 661 551, 534 661 551 lub dotacje@fundacjanh.org` },
];

const SMS_TRIP_TEMPLATES = [
  { label: 'Potwierdźenie rezerwacji', text: (name: string) => `${name}, potwierdźamy rezerwację wycieczki. Szczegóły i regulamin znajdziesz na wycieczki.fundacjanh.org. Kontakt: zwiedzanie@fundacjanh.org | eNHa TRIP` },
  { label: 'Przypomnienie o wycieczce', text: (name: string) => `${name}, przypominamy o jutrzejszej wycieczce! Miejsce zbiórki: os. Centrum D, Kraków-Nowa Huta. Prosimy o punktualność. eNHa TRIP` },
  { label: 'Prośba o potwierdźenie', text: (name: string) => `${name}, prosimy o potwierdźenie uczestnictwa w wycieczce. Odpisz TAK lub NIE. Kontakt: zwiedzanie@fundacjanh.org | eNHa TRIP` },
  { label: 'Informacja o płatności', text: (name: string) => `${name}, prosimy o dokonanie płatności za wycieczkę. Dane przelewu: Fundacja Promocji Nowej Huty, nr konta w mailu. Kontakt: zwiedzanie@fundacjanh.org` },
];

const SMS_TURNUSY_TEMPLATES = [
  { label: 'Potwierdźenie zgłoszenia', text: (name: string) => `${name}, potwierdźamy przyjęcie zgłoszenia na turnus rehabilitacyjny. Skontaktujemy się z informacjami organizacyjnymi. Kontakt: turnusy@fundacjanh.org | Fundacja NH` },
  { label: 'Przypomnienie o dokumentach', text: (name: string) => `${name}, prosimy o dostarczenie orzeczenia o niepełnosprawności i skierowania na turnus. Kontakt: turnusy@fundacjanh.org lub 575 661 551. Fundacja NH` },
  { label: 'Informacja o dofinansowaniu', text: (name: string) => `${name}, prosimy o przesłanie decyzji PFRON/PCPR o dofinansowaniu turnusu. Kontakt: turnusy@fundacjanh.org | Fundacja NH` },
  { label: 'Informacje organizacyjne', text: (name: string) => `${name}, szczegóły turnusu (adres ośrodka, pakowanie, dojazd) wysłaliśmy mailem. W razie pytań: turnusy@fundacjanh.org | Fundacja NH` },
];

const SMS_HUTNIK_TEMPLATES = [
  { label: 'Zaproszenie na spotkanie', text: (name: string) => `${name}, zapraszamy na spotkanie w sprawie współpracy z Hutnik Business Club. Prosimy o kontakt: biuro@fundacjanh.org lub 575 661 551. Fundacja NH` },
  { label: 'Propozycja pakietu', text: (name: string) => `${name}, przygotowaliśmy ofertę pakietu partnerskiego Hutnik Business Club. Szczegóły wysłaliśmy mailem. Kontakt: biuro@fundacjanh.org | Fundacja NH` },
  { label: 'Zaproszenie na mecz VIP', text: (name: string) => `${name}, zapraszamy na mecz Hutnika Nowa Huta w loży VIP! Szczegóły: biuro@fundacjanh.org lub 575 661 551. Hutnik Business Club` },
  { label: 'Przypomnienie o umowie', text: (name: string) => `${name}, prosimy o podpisanie i odesłanie umowy partnerskiej. Kontakt: biuro@fundacjanh.org | Hutnik Business Club` },
];

const SMS_B2B_TEMPLATES = [
  { label: 'Potwierdźenie zapytania', text: (name: string) => `${name}, potwierdźamy otrzymanie zapytania. Nasz specjalista skontaktuje się w ciągu 24h. Kontakt: biuro@fundacjanh.org | Fundacja NH` },
  { label: 'Propozycja współpracy', text: (name: string) => `${name}, przygotowaliśmy ofertę współpracy. Szczegóły wysłaliśmy na maila. Kontakt: biuro@fundacjanh.org lub 575 661 551. Fundacja NH` },
  { label: 'Follow-up', text: (name: string) => `${name}, chcielibyśmy dopytać o szczegóły projektu. Kiedy możemy umówić krótką rozmowę? Kontakt: biuro@fundacjanh.org | Fundacja NH` },
];

const SMS_GRANT_TEMPLATES = [
  { label: 'Status wniosku grantowego', text: (name: string) => `${name}, Twój wniosek grantowy jest w trakcie opracowywania. Skontaktujemy się po zakończeniu analizy. Kontakt: dotacje@fundacjanh.org | Fundacja NH` },
  { label: 'Deadline naboru', text: (name: string) => `${name}, przypominamy o zbliżającym się terminie naboru wniosków! Prosimy o dostarczenie brakujących dokumentów. Kontakt: dotacje@fundacjanh.org` },
  { label: 'Wynik naboru', text: (name: string) => `${name}, mamy informacje o wynikach naboru. Prosimy o kontakt: dotacje@fundacjanh.org lub 575 661 551. Fundacja NH` },
];

const QuickSmsWidget: React.FC<{ phone?: string; applicationId: number; firstName?: string; serviceType?: string }> = ({ phone, applicationId, firstName, serviceType }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const name = firstName || 'Szanowny Kandydacie';

  const handleSend = async () => {
    if (!phone || !message.trim()) return;
    setSending(true);
    try {
      await crmApi.sendSms(phone, message.trim(), applicationId);
      setSent(true);
      setMessage('');
      setTimeout(() => setSent(false), 3000);
    } catch (err: any) {
      toast.error(`Błąd SMS: ${err.response?.data?.error || err.message}`);
    } finally {
      setSending(false);
    }
  };

  if (!phone) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-bold mb-3 flex items-center">
        <Phone size={18} className="mr-2 text-green-600" />
        Szybki SMS
      </h3>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(serviceType === 'wycieczki' ? SMS_TRIP_TEMPLATES
          : serviceType === 'turnusy' ? SMS_TURNUSY_TEMPLATES
          : serviceType === 'hutnik_biznes' ? SMS_HUTNIK_TEMPLATES
          : serviceType === 'marketing_beauty' || serviceType === 'gastro_consulting' ? SMS_B2B_TEMPLATES
          : serviceType === 'granty_fpn' || serviceType === 'dotacje_rozwojowe' ? SMS_GRANT_TEMPLATES
          : isLoanProgram(serviceType) ? SMS_LOAN_TEMPLATES
          : SMS_PUP_TEMPLATES).map((t, i) => (
          <button key={i} onClick={() => setMessage(t.text(name))} className="text-xs bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 px-2.5 py-1 rounded-full transition-colors">
            {t.label}
          </button>
        ))}
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        maxLength={459}
        className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm resize-none"
        placeholder={`SMS do ${phone}...`}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-400">{message.length}/459</span>
        <button
          onClick={handleSend}
          disabled={sending || !message.trim()}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors ${
            sent ? 'bg-green-500 text-white' : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300'
          }`}
        >
          <Send size={14} className="mr-1.5" />
          {sent ? 'Wysłano!' : sending ? 'Wysyłanie...' : 'Wyślij SMS'}
        </button>
      </div>
    </div>
  );
};

// ─── Transfer / Cross-sell Widget ─────────────────────────────────────────────
const TRANSFER_TARGETS: { value: string; label: string }[] = [
  { value: 'pup_grant', label: 'Dotacja PUP' },
  { value: 'wsparcie_na_starcie', label: 'Wsparcie na Starcie' },
  { value: 'pup_and_loan', label: 'PUP + Pożyczka' },
  { value: 'kfs', label: 'KFS' },
  { value: 'dotacje_rozwojowe', label: 'Dotacje Rozwojowe' },
  { value: 'pozyczki_rozwojowe', label: 'Pożyczki Rozwojowe' },
  { value: 'szkolenia', label: 'Szkolenia' },
  { value: 'zus', label: 'ZUS' },
  { value: 'bhp', label: 'BHP (kategoria KFS)' },
  { value: 'bhp_ppoz', label: 'BHP i ppoż (usługi)' },
];

const TransferWidget: React.FC<{ applicationId: number; currentServiceType: string; clientName: string }> = ({ applicationId, currentServiceType, clientName }) => {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [copyNotes, setCopyNotes] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [result, setResult] = useState<{ submissionId: string; targetServiceType: string } | null>(null);

  const targets = TRANSFER_TARGETS.filter(t => t.value !== currentServiceType);

  const handleTransfer = async () => {
    if (!target || !(await askConfirm({ title: `Przenieść ${clientName} do "${targets.find(t => t.value === target)?.label}"?` }))) return;
    setTransferring(true);
    try {
      const res = await authAxios.post(`/api/crm/applications/${applicationId}/transfer`, { targetServiceType: target, copyNotes });
      setResult(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Błąd transferu');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-bold mb-3 flex items-center">
        <ShoppingCart size={18} className="mr-2 text-violet-600" />
        Cross-sell / Transfer
      </h3>
      {result ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <p className="text-green-800 font-medium">Klient przeniesiony!</p>
          <p className="text-green-600 mt-1">Nowy nr: {result.submissionId}</p>
          <button onClick={() => { setResult(null); setOpen(false); setTarget(''); }} className="mt-2 text-xs text-green-700 underline">OK</button>
        </div>
      ) : !open ? (
        <button onClick={() => setOpen(true)} className="w-full bg-violet-50 text-violet-700 border border-violet-200 py-2.5 rounded-lg text-sm font-medium hover:bg-violet-100 transition-colors">
          Przenieś klienta do innego produktu
        </button>
      ) : (
        <div className="space-y-3">
          <select value={target} onChange={e => setTarget(e.target.value)} className="w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-violet-500">
            <option value="">Wybierz produkt docelowy...</option>
            {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={copyNotes} onChange={e => setCopyNotes(e.target.checked)} className="rounded" />
            Kopiuj notatki
          </label>
          <div className="flex gap-2">
            <button onClick={handleTransfer} disabled={!target || transferring} className="flex-1 bg-violet-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:bg-gray-300 transition-colors">
              {transferring ? 'Przenoszenie...' : 'Przenieś'}
            </button>
            <button onClick={() => { setOpen(false); setTarget(''); }} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Anuluj
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Change Requests Panel ───────────────────────────────────────────────────
const CHANGE_FIELD_LABELS: Record<string, string> = {
  pkd_code: 'Kod PKD', industry: 'Branża', business_type: 'Typ działalności',
  premises_address: 'Adres lokalu', premises_title: 'Tytuł prawny lokalu',
  requested_amount: 'Kwota dotacji', loan_amount: 'Kwota pożyczki',
  security_form: 'Forma zabezpieczenia', guarantor_data: 'Dane poręczyciela',
  first_name: 'Imię', last_name: 'Nazwisko', phone: 'Telefon', email: 'Email',
  pesel: 'PESEL', residence_address: 'Adres zamieszkania',
  registration_address: 'Adres zameldowania', marital_status: 'Stan cywilny',
  employment_status: 'Status na rynku pracy', business_idea_raw: 'Pomysł na biznes',
  applicant_category: 'Kategoria wnioskodawcy',
};

function ChangeRequestsPanel({ applicationId, changeRequestPending }: { applicationId: number; changeRequestPending?: boolean }) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!!changeRequestPending);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    crmApi.getChangeRequests(applicationId)
      .then(res => setLogs(res.changeLogs || []))
      .catch(err => console.warn('[DetailSidebar] timeline fetch:', err.message))
      .finally(() => setLoading(false));
  }, [applicationId, expanded]);

  const pendingLogs = logs.filter(l => l.request_status === 'pending');
  const historyLogs = logs.filter(l => l.request_status !== 'pending');

  const handleApprove = async (logId: number) => {
    try {
      await crmApi.approveChange(applicationId, logId);
      setLogs(prev => prev.map(l => l.id === logId ? { ...l, request_status: 'approved' } : l));
    } catch {}
  };

  const handleReject = async (logId: number) => {
    try {
      await crmApi.rejectChange(applicationId, logId);
      setLogs(prev => prev.map(l => l.id === logId ? { ...l, request_status: 'rejected' } : l));
    } catch {}
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <h3 className="font-bold flex items-center gap-2">
          <Clock size={16} className="text-amber-600" />
          Prośby o zmianę
          {pendingLogs.length > 0 && (
            <span className="ml-2 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {pendingLogs.length}
            </span>
          )}
        </h3>
        <span className="text-gray-400 text-sm">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-center text-gray-400 text-sm py-2">Ładowanie...</div>}

          {!loading && pendingLogs.length === 0 && historyLogs.length === 0 && (
            <p className="text-gray-400 text-sm">Brak próśb o zmianę danych.</p>
          )}

          {/* Pending change requests */}
          {pendingLogs.map(log => (
            <div key={log.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-amber-800">
                  {CHANGE_FIELD_LABELS[log.field_name] || log.field_name}
                </span>
                <span className="text-[10px] text-amber-600">{new Date(log.created_at).toLocaleDateString('pl-PL')}</span>
              </div>
              <div className="text-sm mb-2">
                <span className="text-red-600 line-through">{log.old_value || '(puste)'}</span>
                <span className="mx-2 text-gray-400">&rarr;</span>
                <span className="text-green-700 font-medium">{log.new_value || '(puste)'}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(log.id)}
                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  <CheckCircle size={12} /> Zatwierdź
                </button>
                <button
                  onClick={() => handleReject(log.id)}
                  className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                >
                  <XCircle size={12} /> Odrzuć
                </button>
              </div>
            </div>
          ))}

          {/* History (last 10) */}
          {historyLogs.slice(0, 10).map(log => (
            <div key={log.id} className={`border rounded-lg p-3 text-sm ${
              log.request_status === 'approved' ? 'border-green-200 bg-green-50' :
              log.request_status === 'rejected' ? 'border-red-200 bg-red-50' :
              'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">
                  {CHANGE_FIELD_LABELS[log.field_name] || log.field_name}
                </span>
                <span className={`text-[10px] font-medium ${
                  log.request_status === 'approved' ? 'text-green-600' :
                  log.request_status === 'rejected' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {log.request_status === 'approved' ? 'Zatwierdźone' :
                   log.request_status === 'rejected' ? 'Odrzucone' :
                   log.change_type === 'direct' ? 'Zmiana bezpośrednia' : log.request_status}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {log.old_value || '(puste)'} &rarr; {log.new_value || '(puste)'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Widget: Strona WWW klienta ───────────────────────────────────────────────

function CommunicationHub({ applicationId }: { applicationId: number }) {
  const [activeTab, setActiveTab] = React.useState<'timeline' | 'comments' | 'tasks'>('timeline');
  const tabs = [
    { id: 'timeline' as const, label: 'Os czasu', icon: Clock },
    { id: 'comments' as const, label: 'Komentarze', icon: MessageSquare },
    { id: 'tasks' as const, label: 'Zadania', icon: CheckCircle },
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tab header */}
      <div className="flex border-b">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>
      {/* Tab content */}
      <div className="p-4">
        {activeTab === 'timeline' && <UnifiedTimeline applicationId={applicationId} />}
        {activeTab === 'comments' && <CommentsSection applicationId={applicationId} />}
        {activeTab === 'tasks' && <TasksSection applicationId={applicationId} />}
      </div>
    </div>
  );
}

function WebsiteWidget({ applicationId, clientName }: { applicationId: number; clientName?: string }) {
  const [site, setSite] = useState<any>(null);
  const [checked, setChecked] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState('');

  const load = () => {
    authAxios.get(`/api/websites?application_id=${applicationId}`)
      .then(r => setSite(r.data.websites?.[0] ?? null))
      .catch(() => {})
      .finally(() => setChecked(true));
  };

  useEffect(() => { load(); }, [applicationId]);

  const handleProvision = async () => {
    if (!subdomain.trim()) return;
    setProvisioning(true);
    setProvisionError('');
    try {
      const r = await authAxios.post('/api/websites/provision-hostinger', {
        application_id: applicationId,
        client_name: clientName || undefined,
        subdomain: subdomain.trim().toLowerCase(),
      });
      setSite(r.data.site);
      setSubdomain('');
    } catch (err: any) {
      setProvisionError(err.response?.data?.error || err.message);
    } finally {
      setProvisioning(false);
    }
  };

  if (!checked) return null;

  const statusBadge = site ? (
    site.billing_status === 'free'
      ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
          Gratis ({site.days_until_billing}d)
        </span>
      : site.billing_status === 'billing_due'
      ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
          ⚠ Do zafakturowania
        </span>
      : <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
          Zafakturowane
        </span>
  ) : null;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2">
        <Globe size={14} className="text-sky-500" />
        Strona WWW klienta
      </h3>
      {site ? (
        <div className="space-y-2">
          <a href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer"
             className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            {site.domain}
            <ExternalLink size={11} />
          </a>
          <div className="flex items-center gap-2 flex-wrap">
            {statusBadge}
            {site.monthly_price > 0 && (
              <span className="text-xs text-gray-500">{site.monthly_price} zł/mies.</span>
            )}
            <span className="text-xs text-gray-400">{site.hosting_platform}</span>
          </div>
          <a href="/websites" className="text-xs text-gray-400 hover:text-gray-600 block mt-1">
            → Otwórz w module Strony
          </a>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Brak strony. Utwórz automatycznie na Hostinger:</p>
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={subdomain}
              onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="firma-abc"
              className="flex-1 text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-sky-400"
              onKeyDown={e => e.key === 'Enter' && handleProvision()}
            />
            <span className="text-xs text-gray-400 whitespace-nowrap">.krakow.business</span>
          </div>
          {provisionError && (
            <p className="text-xs text-red-600">{provisionError}</p>
          )}
          <button
            type="button"
            onClick={handleProvision}
            disabled={provisioning || !subdomain.trim()}
            className="w-full text-xs bg-sky-500 hover:bg-sky-600 disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium flex items-center justify-center gap-1"
          >
            {provisioning ? (
              <><Loader2 size={11} className="animate-spin" /> Tworzenie...</>
            ) : (
              <><Globe size={11} /> Utwórz na Hostinger</>
            )}
          </button>
          <a href="/websites" className="text-xs text-gray-400 hover:text-gray-600 block text-center">
            lub dodaj ręcznie →
          </a>
        </div>
      )}
    </div>
  );
}

// ── Letter of Intent download with address mode selection ──
const LetterOfIntentDownload: React.FC<{ application: any }> = ({ application }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const hasResidence = !!application.residence_address;
  const hasPremises = !!application.premises_address;

  const downloadLetter = (mode: string) => {
    window.open(`/api/documents/letter-of-intent/${application.id}?address=${mode}`, '_blank');
    setOpen(false);
  };

  const sendLetter = async (mode: string) => {
    setSending(true);
    try {
      await authAxios.post(`/api/documents/letter-of-intent/${application.id}/send`, { addressMode: mode });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err: any) {
      toast.error('Błąd wysyłki: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <Tooltip content="Generuj spersonalizowany List Intencyjny (PDF) z wyborem adresu" position="left">
        <button
          onClick={() => setOpen(!open)}
          className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 text-white py-2.5 rounded-lg font-medium hover:from-amber-700 hover:to-yellow-700 flex items-center justify-center text-sm shadow-md"
        >
          <FileOutput className="mr-2" size={18} />
          {sent ? 'Wysiano!' : 'List Intencyjny'}
        </button>
      </Tooltip>
      {open && (
        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-3 space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 mb-2">Wybierz adres na liscie:</p>

          {hasPremises && (
            <button onClick={() => downloadLetter('premises')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-blue-50 flex items-center gap-2 border border-gray-100">
              <Building2 size={14} className="text-blue-600 flex-shrink-0" />
              <div>
                <span className="font-medium">Adres lokalu firmy</span>
                <p className="text-xs text-gray-500 truncate">{application.premises_address}</p>
              </div>
            </button>
          )}

          {hasResidence && (
            <button onClick={() => downloadLetter('residence')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-green-50 flex items-center gap-2 border border-gray-100">
              <UserCircle size={14} className="text-green-600 flex-shrink-0" />
              <div>
                <span className="font-medium">Adres zamieszkania</span>
                <p className="text-xs text-gray-500 truncate">{application.residence_address}</p>
              </div>
            </button>
          )}

          {hasPremises && hasResidence && (
            <button onClick={() => downloadLetter('both')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-amber-50 flex items-center gap-2 border border-gray-100">
              <LinkIcon size={14} className="text-amber-600 flex-shrink-0" />
              <span className="font-medium">Oba adresy</span>
            </button>
          )}

          {!hasPremises && !hasResidence && (
            <button onClick={() => downloadLetter('premises')} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-50 flex items-center gap-2 border border-gray-100">
              <FileText size={14} className="text-gray-500 flex-shrink-0" />
              <span className="font-medium">Bez adresu</span>
            </button>
          )}

          <div className="border-t pt-1.5 mt-1.5">
            <button
              onClick={() => sendLetter(hasPremises && hasResidence ? 'both' : hasPremises ? 'premises' : 'residence')}
              disabled={sending}
              className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-purple-50 flex items-center gap-2 text-purple-700 border border-purple-100"
            >
              <Send size={14} className="flex-shrink-0" />
              <span className="font-medium">{sending ? 'Wysylanie...' : 'Wyślij emailem do klienta'}</span>
            </button>
          </div>

          <button onClick={() => setOpen(false)} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 pt-1">
            Zamknij
          </button>
        </div>
      )}
    </div>
  );
};

export const DetailSidebar: React.FC<Props> = ({ h }) => {
  const { application, editMode, saving, notes, generatingAI, generatingOptimization, generatingPurchaseList, generatingCertificates, generatingCourseRecommendations, generatingPUPApplication, generatingPDF, sendingEmail, generatingProductAction } = h;

  const { scoringConfig, hasScoringEnabled, minPoints: dynamicMinPoints } = useProductScoring(application?.service_type);

  // Operator assignment state
  const [crmOperators, setCrmOperators] = useState<CrmOperator[]>([]);

  useEffect(() => {
    operatorsApi.getOperators().then(res => setCrmOperators(res.operators || [])).catch(() => {});
  }, []);

  const handleAssignOperator = async (operatorId: number | null) => {
    try {
      await crmApi.assignOperator(application!.id, operatorId);
      // Update local state
      h.loadApplication();
    } catch (err: any) {
      console.error('Assign operator error:', err.message);
    }
  };

  // IPD preview modal state
  const [showIpdPreview, setShowIpdPreview] = useState(false);
  const [ipdPreviewData, setIpdPreviewData] = useState<{ subject: string; html: string; recipient: { email: string; name: string } } | null>(null);
  const [ipdLoading, setIpdLoading] = useState(false);
  const [ipdSending, setIpdSending] = useState(false);

  const handleIpdPreview = async () => {
    if (!application?.id) return;
    setIpdLoading(true);
    try {
      const data = await crmApi.getIpdPreview(application.id);
      setIpdPreviewData(data);
      setShowIpdPreview(true);
    } catch (err: any) {
      console.error('IPD preview error:', err.message);
      toast.error('Nie udało się załadować podglądu IPD');
    } finally {
      setIpdLoading(false);
    }
  };

  const handleIpdSend = async () => {
    if (!application?.id) return;
    if (!(await askConfirm({ title: `Wysłać instrukcje IPD do ${application.first_name} ${application.last_name} (${application.email})?` }))) return;
    setIpdSending(true);
    try {
      const result = await crmApi.sendIpdEmail(application.id);
      if (result.success) {
        toast.success('Email IPD wysłany pomyślnie!');
        setShowIpdPreview(false);
        h.loadApplication();
      } else {
        toast.error('Błąd: ' + result.error);
      }
    } catch (err: any) {
      console.error('IPD send error:', err.message);
      toast.error('Błąd podczas wysyłania emaila IPD');
    } finally {
      setIpdSending(false);
    }
  };

  // OCR state
  const [ocrResults, setOcrResults] = useState<any[]>([]);
  const [ocrApplying, setOcrApplying] = useState<number | null>(null);

  const fetchOcrResults = useCallback(async () => {
    if (!application?.id) return;
    try {
      const response = await crmApi.getOcrResults(application.id);
      if (response.success) setOcrResults(response.results);
    } catch (err: any) {
      console.warn('[DetailSidebar] OCR fetch:', err.message);
    }
  }, [application?.id]);

  useEffect(() => {
    fetchOcrResults();
  }, [fetchOcrResults]);

  const handleOcrApply = async (ocrResult: any) => {
    if (!application?.id) return;
    setOcrApplying(ocrResult.id);
    try {
      const fieldsToApply = ocrResult.extractedFields;
      await crmApi.applyOcrResult(application.id, ocrResult.id, fieldsToApply);
      // Refresh data
      fetchOcrResults();
      h.loadApplication();
    } catch (err: any) {
      console.error('[OCR] apply error:', err.message);
    } finally {
      setOcrApplying(null);
    }
  };

  if (!application) return null;

  const isLoan = isLoanProgram(application.service_type);
  const isPup = isPupProgram(application.service_type);
  const isTrip = application.service_type === 'wycieczki' || application.service_type === 'turnusy';
  const isB2B = ['hutnik_biznes', 'marketing_beauty', 'gastro_consulting'].includes(application.service_type || '');
  const isGrant = ['granty_fpn', 'dotacje_rozwojowe'].includes(application.service_type || '');
  const isService = ['uslugi_ksiegowe', 'krs', 'zus', 'bhp'].includes(application.service_type || '');
  const terminology = getTerminology(application.service_type || '');
  const showFinancial = isPup || isLoan || isGrant || application.service_type === 'czyste_powietrze';
  // Products that use PUP-style AI (23-pt analysis, optimization, purchase list)
  const showPupAiTools = isPup || isLoan || isGrant || application.service_type === 'czyste_powietrze' || application.service_type === 'kfs';
  // All other products get generic AI analysis via product-specific agent configs
  const showGenericAiAnalysis = !showPupAiTools;

  // Sidebar tabs
  const [sidebarTab, setSidebarTab] = useState<'proces' | 'ai' | 'comm' | 'dane'>('proces');

  // Admin section collapsible
  const [adminOpen, setAdminOpen] = useState(false);

  // Duplicate detection
  const [duplicates, setDuplicates] = useState<any[]>([]);
  useEffect(() => {
    if (!application?.id) return;
    const data: any = { excludeId: application.id };
    if (application.email) data.email = application.email;
    if (application.phone) data.phone = application.phone;
    if (application.nip) data.nip = application.nip;
    if (!data.email && !data.phone && !data.nip) return;
    crmApi.checkDuplicates(data).then(r => setDuplicates(r.duplicates || [])).catch(() => {});
  }, [application?.id, application?.email, application?.phone, application?.nip]);

  return (
    <div className="space-y-6">
      {/* Duplicate Alert */}
      {duplicates.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-700 font-medium text-sm mb-2">
            <Copy size={16} />
            Możliwe duplikaty ({duplicates.length})
          </div>
          <div className="space-y-1.5">
            {duplicates.slice(0, 5).map((d: any) => (
              <a
                key={d.id}
                href={`/applications/${d.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs text-orange-600 hover:text-orange-800 hover:underline"
              >
                {d.first_name} {d.last_name} — {d.submission_id || `#${d.id}`}
                <span className="text-orange-400 ml-1">({SERVICE_TYPE_LABELS[d.service_type] || d.service_type})</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ═══ GRUPA 1: Operator & Status ═══ */}

      {/* Operator Notes — na samej gorze */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4">Notatki operatora</h3>
        <MentionsInput value={notes} onChange={(v) => h.setNotes(v)} rows={6} placeholder="Dodaj notatki do wniosku... (wpisz @ aby oznaczyć operatora)" />
        <button onClick={h.handleSaveNotes} className="w-full mt-3 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center">
          <Save className="mr-2" size={16} />
          Zapisz notatki
        </button>
      </div>

      {/* ═══ ALWAYS VISIBLE: Edit + Conditions + Print ═══ */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <div className="flex gap-2">
          {!editMode ? (
            <button onClick={h.handleEnterEditMode} className={`flex-1 bg-gradient-to-r ${isLoan ? 'from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700' : 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'} text-white py-2.5 rounded-lg font-medium flex items-center justify-center text-sm`}>
              <Edit className="mr-1.5" size={16} />
              Edytuj
            </button>
          ) : (
            <>
              <button onClick={h.handleSaveChanges} disabled={saving} className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center text-sm">
                <Save className="mr-1.5" size={16} />
                {saving ? 'Zapisuję...' : 'Zapisz'}
              </button>
              <button onClick={h.handleCancelEdit} disabled={saving} className="bg-gray-500 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-gray-600">
                <X size={16} />
              </button>
            </>
          )}
        </div>
        {(isPup || isLoan) && (
          <div className="space-y-2">
            <ConditionsSection application={application} onUpdate={h.loadApplication} />
            <PrintFlagsSection application={application} onUpdate={h.loadApplication} />
          </div>
        )}
      </div>

      {/* ═══ TAB BAR ═══ */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex border-b">
          {([
            { id: 'proces' as const, label: 'Proces', Icon: ClipboardCheck },
            { id: 'ai' as const, label: 'AI & Akcje', Icon: Sparkles },
            { id: 'comm' as const, label: 'Komunikacja', Icon: MessageSquare },
            { id: 'dane' as const, label: 'Dane', Icon: FileText },
          ]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setSidebarTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-2.5 text-xs font-medium transition-colors ${
                sidebarTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.Icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ TAB 1: PROCES ═══ */}
      {sidebarTab === 'proces' && (<>

      {/* Product Roadmap */}
      <ProductRoadmap
        productCode={application.service_type || 'pup_grant'}
        currentStatus={(() => {
          const prog = getProgramByServiceType(application.service_type);
          const field = prog?.kanbanStatusField || 'status';
          return (application as any)[field] || application.status || 'nowe_zgloszenie';
        })()}
        accentColor={getProgramByServiceType(application.service_type)?.color || 'blue'}
      />

      {/* Lista zakupów przeniesiona do centralnego panelu (CRMApplicationDetail.tsx) */}

      {/* Document Checklist */}
      <DocumentChecklistWidget
        applicationId={application.id}
        accentColor={getProgramByServiceType(application.service_type)?.color || 'blue'}
      />

      {/* Status Management */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4">Status wniosku</h3>
        <select value={application.status} onChange={(e) => h.handleStatusChange(e.target.value)} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 mb-2" aria-label="Status wniosku">
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        {isLoan && (
          <div className="mt-2">
            <label className="block text-sm font-medium text-emerald-700 mb-1">Status pożyczki</label>
            <select value={application.loan_status || ''} onChange={(e) => h.handleLoanStatusChange(e.target.value)} className="w-full p-3 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-emerald-50" aria-label="Status pożyczki">
              <option value="">Nie ustawiony</option>
              {Object.entries(LOAN_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Scoring / Punktacja (moved from Dane tab — logically belongs with process) */}
      {(isPup || hasScoringEnabled) && (() => {
        const minPts = isPup ? 22 : (dynamicMinPoints ?? 23);
        const maxPts = isPup ? 36 : (dynamicMinPoints ? dynamicMinPoints * 2 : 23);
        return (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold mb-4">{isPup ? 'Punktacja PUP 2026' : 'Punktacja'}</h3>
          {application.points !== undefined && application.points !== null && (
            <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">{isPup ? 'Punktacja PUP 2026:' : 'Punktacja:'}</span>
                <span className={`text-2xl font-bold ${application.points >= minPts ? 'text-green-600' : 'text-red-600'}`}>{application.points}/{maxPts}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div className={`h-2 rounded-full ${application.points >= minPts ? 'bg-green-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, (application.points / maxPts) * 100)}%` }}></div>
              </div>
              {application.points >= minPts
                ? <p className="text-sm text-green-700 mt-1">✓ Kwalifikuje się (min. {minPts} pkt)</p>
                : <p className="text-sm text-red-700 mt-1">✗ Za mało punktów (brakuje {minPts - application.points} pkt do min. {minPts})</p>}
            </div>
          )}
          <Tooltip content={isPup ? 'Kalkulacja wg regulaminu GUP Kraków 2026 (7 kategorii, 36 pkt max)' : 'Szybka kalkulacja punktów.'} position="left">
            <button onClick={h.handleCalculatePoints} className="w-full bg-green-600 text-white py-2.5 rounded-lg font-medium hover:bg-green-700 flex items-center justify-center text-sm">
              <Calculator className="mr-2" size={18} />
              Oblicz punkty ({minPts} min / {maxPts} max)
            </button>
          </Tooltip>
        </div>
        );
      })()}

      {/* Operator Assignment */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-2 flex items-center gap-2">
          <UserCircle size={18} />
          Przydzielony operator
        </h3>
        <select
          aria-label="Przydzielony operator"
          value={application.assigned_operator_id || ''}
          onChange={(e) => {
            const val = e.target.value;
            handleAssignOperator(val ? parseInt(val, 10) : null);
          }}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">— Brak (nieprzydzielony) —</option>
          {crmOperators.map(op => (
            <option key={op.id} value={op.id}>{op.name} ({op.role})</option>
          ))}
        </select>
        {application.assigned_operator_name && (
          <p className="text-xs text-gray-500 mt-1">Aktualnie: {application.assigned_operator_name}</p>
        )}
      </div>

      {/* AI Next Best Action */}
      <NextBestActionWidget applicationId={application.id} />

      </>)}

      {/* ═══ TAB 2: AI & AKCJE ═══ */}
      {sidebarTab === 'ai' && (<>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <Sparkles size={18} className="text-purple-500" />
          Narzędzia AI
        </h3>
        <div className="space-y-3">
          {/* AI Analysis — PUP or generic */}
          {showPupAiTools && (
            <Tooltip content={isLoan ? "AI analiza wniosku pożyczkowego - ocena szans, ryzyka, rekomendacje." : isGrant ? "AI analiza wniosku dotacyjnego - kwalifikowalność, budżet." : "Automatyczna ocena 23-punktowa według regulaminu PUP Kraków 2026."} position="left">
              <button onClick={h.handleGenerateAI} disabled={generatingAI} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center">
                <Sparkles className="mr-2" size={20} />
                {generatingAI ? 'Generowanie...' : 'Generuj analizę AI'}
              </button>
            </Tooltip>
          )}

          {showGenericAiAnalysis && (
            <Tooltip content="AI analiza dopasowana do produktu — ocena, mocne strony, ryzyka, rekomendacje." position="left">
              <button onClick={h.handleGenerateProductAnalysis} disabled={generatingAI} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center">
                <Sparkles className="mr-2" size={20} />
                {generatingAI ? 'Generowanie...' : 'Generuj analizę AI'}
              </button>
            </Tooltip>
          )}

          {/* Per-product AI quick actions — SINGLE SOURCE */}
          {PRODUCT_AI_ACTIONS[application.service_type || '']?.map(action => (
            <Tooltip key={action.type} content={action.tooltip} position="left">
              <button
                onClick={() => h.handleProductAIAction(action.type)}
                disabled={h.generatingProductAction}
                className={`w-full ${action.gradient} text-white py-3 rounded-lg font-medium hover:opacity-90 disabled:bg-gray-400 flex items-center justify-center shadow-lg`}
              >
                {action.icon === 'FileText' && <FileText className="mr-2" size={20} />}
                {action.icon === 'ClipboardCheck' && <ClipboardCheck className="mr-2" size={20} />}
                {action.icon === 'Calculator' && <Calculator className="mr-2" size={20} />}
                {action.icon === 'TrendingUp' && <TrendingUp className="mr-2" size={20} />}
                {action.icon === 'Globe' && <Globe className="mr-2" size={20} />}
                {action.icon === 'BookOpen' && <BookOpen className="mr-2" size={20} />}
                {action.icon === 'Award' && <Award className="mr-2" size={20} />}
                {h.generatingProductAction ? 'Generowanie...' : action.label}
              </button>
            </Tooltip>
          ))}

          {/* AI Assistant */}
          <Tooltip content={isTrip ? "Zadaj pytania o rezerwację, trasę, dostępność przewodników." : isB2B ? "Zadaj pytania o klienta, ofertę, współpracę." : isGrant ? "Zadaj pytania o dotację, nabory, budżet." : "Zadaj pytania o wniosek, uzyskaj rekomendacje od AI."} position="left">
            <button onClick={() => h.setShowAIAssistant(true)} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 flex items-center justify-center shadow-lg">
              <MessageSquare className="mr-2" size={20} />
              {isTrip ? 'Asystent eNHa TRIP' : isB2B ? 'Asystent Biznesowy' : isGrant ? 'Asystent Dotacyjny' : isService ? 'Asystent Usługowy' : 'Otwórz Asystenta AI'}
            </button>
          </Tooltip>

          {/* Marketing AI Studio */}
          <Tooltip content="Generuj materiały marketingowe: posty, ulotki, oferty, reklamy, case study — z wizualizacją i opcją publikacji." position="left">
            <button onClick={() => h.setShowMarketingGenerator(true)} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-3 rounded-lg font-medium hover:from-pink-600 hover:to-rose-600 flex items-center justify-center shadow-lg">
              <Sparkles className="mr-2" size={20} />
              Marketing AI Studio
            </button>
          </Tooltip>

          {/* PUP-only AI tools */}
          {showPupAiTools && (
            <>
              <Tooltip content="Szczegółowy raport optymalizacji wniosku." position="left">
                <button onClick={h.handleGenerateOptimization} disabled={generatingOptimization} className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-teal-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <TrendingUp className="mr-2" size={20} />
                  {generatingOptimization ? 'Optymalizacja...' : 'Raport Optymalizacji'}
                </button>
              </Tooltip>
              <Tooltip content={isLoan ? "Lista zakupów z budżetem do 175 000 zł." : "Generuj kompletną listę zakupów z linkami."} position="left">
                <button onClick={h.handleGeneratePurchaseList} disabled={generatingPurchaseList} className="w-full bg-gradient-to-r from-orange-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-orange-700 hover:to-pink-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <ShoppingCart className="mr-2" size={20} />
                  {generatingPurchaseList ? 'Generowanie...' : 'Lista Zakupów AI'}
                </button>
              </Tooltip>
              <Tooltip content="Pobierz wpisane zakupy jako CSV (otwarcie w Excel)." position="left">
                <button onClick={async () => {
                  try {
                    const token = localStorage.getItem('grant_crm_token');
                    const resp = await fetch(`/api/crm/applications/${application.id}/purchases-export`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!resp.ok) { toast.error(resp.status === 404 ? 'Brak zakupów do eksportu' : 'Błąd eksportu'); return; }
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = `Zakupy_${application.first_name}_${application.last_name}.csv`; a.click(); URL.revokeObjectURL(url);
                  } catch { toast.error('Błąd eksportu zakupów'); }
                }} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-cyan-700 flex items-center justify-center shadow-lg">
                  <Download className="mr-2" size={20} />
                  Eksportuj zakupy (CSV)
                </button>
              </Tooltip>
            </>
          )}

          {/* ── NEW AI TOOLS: Forecast, Tax, Market ── */}
          {showPupAiTools && (
            <>
              <Tooltip content="Prognoza finansowa 3-letnia: przychody, koszty, break-even, cashflow." position="left">
                <button onClick={h.handleGenerateFinancialForecast} disabled={h.generatingForecast} className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <BarChart3 className="mr-2" size={20} />
                  {h.generatingForecast ? 'Generowanie...' : 'Prognoza Finansowa 3Y'}
                </button>
              </Tooltip>
              <Tooltip content="Doradca podatkowy: porównanie ryczałtu, KPiR, pełnej księgowości + rekomendacja." position="left">
                <button onClick={h.handleGenerateTaxAdvice} disabled={h.generatingTaxAdvice} className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 text-white py-3 rounded-lg font-medium hover:from-amber-700 hover:to-yellow-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <Calculator className="mr-2" size={20} />
                  {h.generatingTaxAdvice ? 'Analizowanie...' : 'Doradca Podatkowy'}
                </button>
              </Tooltip>
              <Tooltip content="Raport rynkowy: konkurencja, grupa docelowa, SWOT, strategia cenowa." position="left">
                <button onClick={h.handleGenerateMarketResearch} disabled={h.generatingMarketResearch} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-violet-700 hover:to-purple-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <Globe className="mr-2" size={20} />
                  {h.generatingMarketResearch ? 'Badanie rynku...' : 'Raport Rynkowy'}
                </button>
              </Tooltip>
            </>
          )}

          {isPup && (
            <>
              <Tooltip content="Generuj przykładowe zaświadczenia: praktyki, wolontariat, referencje." position="left">
                <button onClick={() => h.handleGenerateCertificates('all')} disabled={generatingCertificates} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <Award className="mr-2" size={20} />
                  {generatingCertificates ? 'Generowanie...' : 'Zaświadczenia'}
                </button>
              </Tooltip>
              <Tooltip content="Użyj AI do wygenerowania treści zaświadczenia." position="left">
                <button onClick={() => h.setShowCertificateForm(true)} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 flex items-center justify-center shadow-lg">
                  <Sparkles className="mr-2" size={20} />
                  Zaświadczenia z AI
                </button>
              </Tooltip>
              <Tooltip content="AI zaproponuje kursy Fundacji NH dopasowane do biznesu kandydata." position="left">
                <button onClick={h.handleRecommendCourses} disabled={generatingCourseRecommendations} className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-cyan-700 hover:to-blue-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <BookOpen className="mr-2" size={20} />
                  {generatingCourseRecommendations ? 'Generowanie...' : 'Kursy Fundacji NH'}
                </button>
              </Tooltip>
              <Tooltip content="Generuj indywidualny kupon 100% na kurs online + konto na platformie. Rotacja: Akademia Kompetencji / Aspire / Fundacja NH." position="left">
                <PupCouponButton applicationId={application.id} clientEmail={application.email} clientName={`${application.first_name} ${application.last_name}`} />
              </Tooltip>
              <CompletedCoursesPanel applicationId={application.id} />
            </>
          )}

          {/* Loan-only tools */}
          {isLoan && (
            <>
              <Tooltip content="AI wygeneruje kompletny biznesplan pod wymagania operatora pożyczkowego." position="left">
                <button onClick={h.handleGenerateLoanBusinessPlan} disabled={h.generatingLoanBusinessPlan} className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <FileText className="mr-2" size={20} />
                  {h.generatingLoanBusinessPlan ? 'Generowanie...' : 'Generuj Biznesplan'}
                </button>
              </Tooltip>
              <Tooltip content="AI wygeneruje szczegółowy kosztorys wykorzystania pożyczki." position="left">
                <button onClick={h.handleGenerateLoanCostEstimate} disabled={h.generatingLoanCostEstimate} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 rounded-lg font-medium hover:from-teal-700 hover:to-cyan-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <Banknote className="mr-2" size={20} />
                  {h.generatingLoanCostEstimate ? 'Generowanie...' : 'Generuj Kosztorys'}
                </button>
              </Tooltip>
              <Tooltip content="Tabela amortyzacji z harmonogramem rat (1.45% rocznie)." position="left">
                <button onClick={h.handleGenerateRepaymentSchedule} disabled={h.generatingRepaymentSchedule} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <CalendarClock className="mr-2" size={20} />
                  {h.generatingRepaymentSchedule ? 'Generowanie...' : 'Harmonogram Spłat'}
                </button>
              </Tooltip>
              <Tooltip content="Generuj formularz wniosku do operatora pożyczkowego (PFP/FARR/FGSA/FRW/KSWP)." position="left">
                <button onClick={h.handleGenerateOperatorApplication} disabled={h.generatingOperatorApp} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-violet-700 hover:to-purple-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <Building2 className="mr-2" size={20} />
                  {h.generatingOperatorApp ? 'Generowanie...' : 'Wniosek do Operatora'}
                </button>
              </Tooltip>
              <Tooltip content="Sprawdź kompletność dokumentów wymaganych przez operatora." position="left">
                <button onClick={h.handleLoanCompletenessCheck} disabled={h.checkingLoanCompleteness} className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-lg font-medium hover:from-amber-700 hover:to-orange-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <ClipboardCheck className="mr-2" size={20} />
                  {h.checkingLoanCompleteness ? 'Sprawdźanie...' : 'Kompletność Dokumentów'}
                </button>
              </Tooltip>
              <Tooltip content="Generuj umowę współpracy Fundacja-Klient (5% netto / min. 8200 zł)." position="left">
                <button onClick={h.handleGenerateLoanContract} disabled={h.generatingLoanContract} className="w-full bg-gradient-to-r from-rose-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-rose-700 hover:to-pink-700 disabled:bg-gray-400 flex items-center justify-center shadow-lg">
                  <FileCheck className="mr-2" size={20} />
                  {h.generatingLoanContract ? 'Generowanie...' : 'Umowa Pożyczkowa'}
                </button>
              </Tooltip>
              {application.loan_amount && (
                <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                  <p className="text-xs font-semibold text-emerald-800 mb-1">Kalkulacja prowizji</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">5% netto:</span>
                    <span className="font-bold text-emerald-700">{Math.max(8200, Math.round(Number(application.loan_amount) * 0.05)).toLocaleString('pl-PL')} zł</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Min. 8 200 zł brutto</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Unified Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-3 text-sm">Szybkie akcje</h3>
        <div className="space-y-2">
          {/* Admin panel link (external products) */}
          {ADMIN_PANEL_URLS[application.service_type || ''] && (
            <a href={ADMIN_PANEL_URLS[application.service_type || '']} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm hover:bg-gray-100 transition-colors border border-gray-200">
              <ExternalLink size={14} /> Panel administracyjny
            </a>
          )}

          {/* Unified welcome email */}
          <button onClick={h.handleSendWelcomeEmail} disabled={sendingEmail} className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50">
            <Mail size={14} /> {sendingEmail ? 'Wysyłanie...' : (WELCOME_EMAIL_LABELS[application.service_type || ''] || 'Wyślij email powitalny')}
          </button>

          {/* Unified document package (if applicable) */}
          {DOC_PACKAGE_LABELS[application.service_type || ''] && (
            <button onClick={h.handleSendDocumentPackage} disabled={sendingEmail} className="w-full flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors border border-green-200 disabled:opacity-50">
              <Send size={14} /> {sendingEmail ? 'Wysyłanie...' : DOC_PACKAGE_LABELS[application.service_type || '']}
            </button>
          )}

          {/* IPD Instructions (PUP only) */}
          {isPup && (
            <button onClick={handleIpdPreview} disabled={ipdLoading} className="w-full flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm hover:bg-indigo-100 transition-colors border border-indigo-200 disabled:opacity-50">
              <FileText size={14} /> {ipdLoading ? 'Ładowanie...' : 'Instrukcje IPD'}
            </button>
          )}

          {/* Client Portal link */}
          <Tooltip content="Wygeneruj unikalny link dla klienta do samodzielnego przesyłania dokumentów bez logowania." position="left">
            <button onClick={() => h.setShowClientLinkModal(true)} className="w-full flex items-center gap-2 px-3 py-2 bg-orange-50 text-orange-700 rounded-lg text-sm hover:bg-orange-100 transition-colors border border-orange-200">
              <LinkIcon size={14} /> Link dla Klienta
            </button>
          </Tooltip>

          {/* Google Drive */}
          {application.gdrive_folder_id && (
            <button onClick={() => window.open(`https://drive.google.com/drive/folders/${application.gdrive_folder_id}`, '_blank')} className="w-full flex items-center gap-2 px-3 py-2 bg-cyan-50 text-cyan-700 rounded-lg text-sm hover:bg-cyan-100 transition-colors border border-cyan-200">
              <FolderOpen size={14} /> Folder Google Drive
            </button>
          )}
        </div>
      </div>

      {/* Token Link Display */}
      {application.upload_token && (
        <div className="p-4 bg-gradient-to-r from-orange-50 to-pink-50 border-2 border-orange-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700 flex items-center">
              <LinkIcon className="mr-1" size={16} />
              Portal Klienta
            </span>
            {application.token_expires_at && (
              <span className="text-xs text-gray-500">{new Date(application.token_expires_at) > new Date() ? 'Aktywny' : 'Wygasł'}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.open(`${window.location.origin}/portal/${application.upload_token}`, '_blank')} className="flex-1 bg-orange-500 text-white py-2 px-3 rounded text-xs hover:bg-orange-600 flex items-center justify-center" title="Otwórz portal klienta">
              <ExternalLink size={14} className="mr-1" />
              Otwórz
            </button>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/portal/${application.upload_token}`); }} className="bg-orange-400 text-white py-2 px-3 rounded text-xs hover:bg-orange-500" title="Kopiuj link">
              <Copy size={14} />
            </button>
          </div>
          {application.token_last_used_at && (
            <p className="text-xs text-gray-500 mt-2">Ostatnie użycie: {new Date(application.token_last_used_at).toLocaleString('pl-PL')}</p>
          )}
        </div>
      )}

      {/* Industry Intelligence */}
      {(showPupAiTools || isB2B || isGrant || isService || application.service_type === 'czyste_powietrze' || application.service_type === 'kfs') && (
        <IndustryIntelligencePanel
          applicationId={application.id}
          pkdCode={application.pkd_code}
          businessDescription={application.business_idea_raw}
          firstName={application.first_name}
          lastName={application.last_name}
        />
      )}

      {/* plannedPurchases — przeniesione do TAB 1 (PurchasesSection) */}

      {/* Website Widget */}
      {!isTrip && !isService && (
        <WebsiteWidget applicationId={application.id} clientName={`${application.first_name || ''} ${application.last_name || ''}`.trim() || undefined} />
      )}

      {/* Letter of Intent (PUP) */}
      {isPup && <LetterOfIntentDownload application={application} />}

      </>)}

      {/* ═══ TAB 3: KOMUNIKACJA ═══ */}
      {sidebarTab === 'comm' && (<>

      {/* Gmail Correspondence */}
      {h.application?.email && (
        <GmailCorrespondence
          clientEmail={h.application.email}
          clientName={`${h.application.first_name || ''} ${h.application.last_name || ''}`.trim()}
          defaultExpanded={true}
        />
      )}

      {/* Portal Chat v2 */}
      {h.application?.email && (
        <PortalChatPanel
          applicationId={h.application.id}
          clientName={`${h.application.first_name || ''} ${h.application.last_name || ''}`.trim()}
        />
      )}

      {/* Portal Meetings */}
      {h.application?.email && (
        <PortalMeetingsPanel
          applicationId={h.application.id}
          clientName={`${h.application.first_name || ''} ${h.application.last_name || ''}`.trim()}
        />
      )}

      {/* Client Chat */}
      {h.application?.email && (
        <ClientChat
          applicationId={h.application.id}
          clientName={`${h.application.first_name || ''} ${h.application.last_name || ''}`.trim()}
        />
      )}

      {/* Quick SMS */}
      <QuickSmsWidget phone={application.phone} applicationId={application.id} firstName={application.first_name} serviceType={application.service_type} />

      {/* Communication Hub */}
      <CommunicationHub applicationId={application.id} />

      {/* Follow-up Widget */}
      <div className="bg-white rounded-lg shadow p-4">
        <FollowUpWidget applicationId={application.id} />
      </div>

      </>)}

      {/* ═══ TAB 4: DANE ═══ */}
      {sidebarTab === 'dane' && (<>

      {/* ═══ GRUPA 3: Dokumenty & Generatory ═══ */}

      {/* Invoices (Fakturownia) — all products */}
      <InvoicesSection
        applicationId={application.id}
        buyerName={`${application.first_name} ${application.last_name}`}
        buyerEmail={application.email}
        buyerPhone={application.phone}
        buyerNip={application.nip}
        serviceType={application.service_type}
      />

      {/* Contract Acceptance — PUP/Loans/Grants only */}
      {(showPupAiTools || isGrant) && (
        <ContractAcceptanceSection
          applicationId={application.id}
          applicationEmail={application.email}
          applicationPhone={application.phone}
          firstName={application.first_name}
          lastName={application.last_name}
        />
      )}

      {/* (Industry Intel + NBA moved to Tab Proces / Tab AI) */}

      {/* PUP Documents */}
      {isPup && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold mb-4">Dokumenty PUP</h3>
          <div className="space-y-2">
            <Tooltip content="Pobierz wzór umowy z wypełnionymi danymi kandydata" position="left">
              <button onClick={h.handleDownloadContract} className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center text-sm">
                <FileText className="mr-2" size={18} />
                Pobierz umowę PUP
              </button>
            </Tooltip>
            <Tooltip content="Lista wszystkich dokumentów wymaganych do złożenia wniosku PUP" position="left">
              <button onClick={h.handleDownloadDocuments} className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 flex items-center justify-center text-sm">
                <Download className="mr-2" size={18} />
                Pobierz listę dokumentów
              </button>
            </Tooltip>
            <Tooltip content="AI wygeneruje kompletny wniosek PUP (2000-4000 słów)." position="left">
              <button onClick={h.handleGeneratePUPApplication} disabled={generatingPUPApplication} className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-2.5 rounded-lg font-medium hover:from-green-700 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-500 flex items-center justify-center text-sm shadow-md">
                <FileText className="mr-2" size={18} />
                {generatingPUPApplication ? 'Generowanie...' : 'Generuj wniosek PUP'}
              </button>
            </Tooltip>
            <Tooltip content="Wniosek PUP jako PDF z formatowaniem." position="left">
              <button onClick={h.handleGeneratePUPApplicationPDF} disabled={generatingPDF} className="w-full bg-gradient-to-r from-red-600 to-pink-600 text-white py-2.5 rounded-lg font-medium hover:from-red-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 flex items-center justify-center text-sm shadow-md">
                <FileType className="mr-2" size={18} />
                    {generatingPDF ? 'Generowanie PDF...' : 'Generuj wniosek PUP (PDF)'}
              </button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Szablony & Eksport */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4">Szablony i eksport</h3>
        <div className="space-y-2">
          <Tooltip content="Szablony email do komunikacji z kandydatem" position="left">
            <button onClick={() => h.setShowEmailTemplates(true)} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 flex items-center justify-center text-sm shadow-md">
              <Mail className="mr-2" size={18} />
              Szablony Email
            </button>
          </Tooltip>
          <Tooltip content="Gotowe szablony dokumentów DOCX" position="left">
            <button onClick={() => h.setShowDocumentTemplates(true)} className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-2.5 rounded-lg font-medium hover:from-orange-700 hover:to-red-700 flex items-center justify-center text-sm shadow-md">
              <Folder className="mr-2" size={18} />
              Szablony dokumentów
            </button>
          </Tooltip>
          <Tooltip content="Generuj listy intencyjne, deklaracje z danymi kandydata" position="left">
            <button onClick={() => h.setShowDocumentGenerator(true)} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 flex items-center justify-center text-sm shadow-md">
              <FileOutput className="mr-2" size={18} />
              Generator dokumentów z CRM
            </button>
          </Tooltip>
          <Tooltip content="Eksportuj wnioski do pliku Excel (.xlsx)" position="left">
            <button onClick={h.handleExportToExcel} disabled={saving} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2.5 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 flex items-center justify-center text-sm shadow-md">
              <FileSpreadsheet className="mr-2" size={18} />
              {saving ? 'Eksportowanie...' : 'Eksport do Excel'}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Financial Info — PUP/Loans/Grants/CzystePowietrze only */}
      {showFinancial && <div className="bg-white rounded-lg shadow p-6">
        <h3 className="font-bold mb-4">Informacje finansowe</h3>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-gray-600">{isLoan ? 'Kwota pożyczki' : 'Kwota wnioskowana'}</p>
            <p className={`text-2xl font-bold ${isLoan ? 'text-emerald-600' : 'text-blue-600'}`}>{(isLoan ? (application.loan_amount ?? application.requested_amount ?? 0) : (application.requested_amount ?? 0)).toLocaleString('pl-PL')} zł</p>
          </div>
          {isPup && (
            <>
              <div>
                <p className="text-gray-600">Forma zabezpieczenia</p>
                <p className="font-medium">{application.security_form || 'Nie wybrano'}</p>
              </div>
              {application.security_form === 'blokada_konta' && (
                <div>
                  <p className="text-gray-600">Kwota blokady</p>
                  <p className="font-bold text-orange-600">{(application.blockadeAmount || 0).toLocaleString('pl-PL')} zł</p>
                  <p className="text-xs text-gray-500">(+9.25% odsetki)</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>}

      {/* Loan Info (Wsparcie na Starcie) */}
      {isLoan && (
        <LoanInfoSection application={application} onSave={async (data: Record<string, any>) => {
          await crmApi.updateApplication(application.id, data);
          h.loadApplication();
        }} />
      )}

      {/* Loan Calculator */}
      {isLoan && (
        <LoanCalculatorWidget
          loanAmount={Number(application.loan_amount) || 0}
          repaymentPeriodYears={Number(application.repayment_period) || 7}
          gracePeriodMonths={Number(application.grace_period) || 0}
          interestRate={Number(application.interest_rate) || 0.001}
          disbursementDate={application.disbursement_date}
        />
      )}

      {/* Guarantors */}
      {isLoan && <GuarantorsSection applicationId={application.id} />}

      {/* ═══ GRUPA 6: Dane produktowe ═══ */}

      {/* ═══ KFS: Company & Training Info ═══ */}
      {application.service_type === 'kfs' && (
        <KfsInfoSection application={application} onSave={async (data: Record<string, any>) => {
          await crmApi.updateApplication(application.id, data);
          h.loadApplication();
        }} />
      )}

      {/* ═══ ZUS: Debt & Financial Info ═══ */}
      {application.service_type === 'zus' && (
        <ZusInfoSection application={application} onSave={async (data: Record<string, any>) => {
          await crmApi.updateApplication(application.id, data);
          h.loadApplication();
        }} />
      )}

      {/* ═══ BHP: Training Info ═══ */}
      {application.service_type === 'bhp' && (
        <BhpInfoSection application={application} onSave={async (data: Record<string, any>) => {
          await crmApi.updateApplication(application.id, data);
          h.loadApplication();
        }} />
      )}

      {/* ═══ Pożyczka Rozwojowa: Business Info ═══ */}
      {application.service_type === 'pozyczki_rozwojowe' && (
        <DevLoanInfoSection application={application} onSave={async (data: Record<string, any>) => {
          await crmApi.updateApplication(application.id, data);
          h.loadApplication();
        }} />
      )}

      {/* Loan Completeness Report Modal */}
      {isLoan && h.showLoanCompleteness && h.loanCompletenessReport && (
        <div className="bg-white rounded-lg shadow p-6 border-2 border-amber-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-amber-800">Raport kompletności</h3>
            <button onClick={() => h.setShowLoanCompleteness(false)} className="text-gray-400 hover:text-gray-600" title="Zamknij raport">
              <X size={16} />
            </button>
          </div>
          <div className="text-sm whitespace-pre-wrap text-gray-700">{typeof h.loanCompletenessReport === 'string' ? h.loanCompletenessReport : JSON.stringify(h.loanCompletenessReport, null, 2)}</div>
        </div>
      )}

      {/* OCR Results */}
      {ocrResults.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
            <Scan size={16} className="text-purple-600" />
            OCR — Odczytane dane ({ocrResults.length})
          </h3>
          <div className="space-y-2">
            {ocrResults.map((ocr: any) => {
              const fieldCount = Object.keys(ocr.extractedFields || {}).length;
              const isApplied = ocr.status === 'applied';
              const isFailed = ocr.status === 'failed';
              const isProcessing = ocr.status === 'processing';

              return (
                <div key={ocr.id} className={`bg-white rounded-lg border p-3 text-sm ${isApplied ? 'border-green-200 bg-green-50' : isFailed ? 'border-red-200 bg-red-50' : 'border-purple-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-800 truncate">{ocr.fileName}</span>
                    {isApplied && <CheckCircle size={14} className="text-green-600" />}
                    {isFailed && <XCircle size={14} className="text-red-400" />}
                    {isProcessing && <Loader2 size={14} className="text-purple-500 animate-spin" />}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{ocr.documentType}</span>
                    <span>·</span>
                    <span>Pewność: {ocr.confidence}%</span>
                    <span>·</span>
                    <span>{fieldCount} pól</span>
                  </div>
                  {ocr.status === 'completed' && fieldCount > 0 && (
                    <div className="mt-2">
                      <div className="bg-purple-50 rounded p-2 mb-2">
                        {Object.entries(ocr.extractedFields).slice(0, 5).map(([key, val]: [string, any]) => (
                          <div key={key} className="flex justify-between text-xs py-0.5">
                            <span className="text-gray-500">{key}:</span>
                            <span className="text-gray-800 font-medium truncate ml-2">{String(val)}</span>
                          </div>
                        ))}
                        {fieldCount > 5 && (
                          <div className="text-xs text-gray-400 mt-1">+{fieldCount - 5} więcej pól</div>
                        )}
                      </div>
                      <button
                        onClick={() => handleOcrApply(ocr)}
                        disabled={ocrApplying === ocr.id}
                        className="w-full text-xs bg-purple-600 text-white rounded py-1.5 hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {ocrApplying === ocr.id ? 'Stosowanie...' : 'Zastosuj do wniosku'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══ GRUPA 7: Admin & Historia (collapsible) ═══ */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          onClick={() => setAdminOpen(!adminOpen)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <span className="font-bold text-sm text-gray-500">Admin & Historia</span>
          {adminOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>
        {adminOpen && (
          <div className="px-4 pb-4 space-y-6">
            {/* Transfer / Cross-sell */}
            <TransferWidget applicationId={application.id} currentServiceType={application.service_type || 'pup_grant'} clientName={`${application.first_name} ${application.last_name}`} />

            {/* Change Requests */}
            <ChangeRequestsPanel applicationId={application.id} changeRequestPending={application.change_request_pending} />

            {/* Communication History */}
            <CommunicationHistoryPanel applicationId={application.id} />

            {/* Tags */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-3">Tagi</h3>
              <TagSelector applicationId={application.id} />
            </div>

            {/* Status Timeline */}
            <StatusTimeline applicationId={application.id} />

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-bold mb-4">Metadane</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-600">Data złożenia</p>
                  <p className="font-medium">{new Date(application.created_at).toLocaleString('pl-PL')}</p>
                </div>
                <div>
                  <p className="text-gray-600">Ostatnia aktualizacja</p>
                  <p className="font-medium">{new Date(application.updated_at!).toLocaleString('pl-PL')}</p>
                </div>
                {application.service_type && (
                  <div>
                    <p className="text-gray-600">Program</p>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${SERVICE_TYPE_COLORS[application.service_type] || 'bg-gray-100 text-gray-600'}`}>
                      {SERVICE_TYPE_LABELS[application.service_type] || application.service_type}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Delete Application — at the very bottom */}
            <div className="bg-white rounded-lg shadow p-6 border-2 border-red-100">
              <h3 className="font-bold mb-4 text-red-700">Strefa Niebezpieczna</h3>
              <Tooltip content={`UWAGA: Ta operacja jest nieodwracalna. Usuń ${terminology.singular} tylko gdy masz pewność.`} position="left">
                <button onClick={h.handleDeleteApplication} disabled={saving} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center">
                  <X className="mr-2" size={20} />
                  Usuń {terminology.singular.charAt(0).toUpperCase() + terminology.singular.slice(1)}
                </button>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      </>)}

      {/* IPD Preview Modal */}
      {showIpdPreview && ipdPreviewData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowIpdPreview(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-xl">
              <div>
                <h3 className="font-bold text-gray-900">Podgląd: Instrukcje IPD</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Do: {ipdPreviewData.recipient.name} ({ipdPreviewData.recipient.email})
                </p>
                <p className="text-xs text-gray-500">
                  Temat: {ipdPreviewData.subject}
                </p>
              </div>
              <button onClick={() => setShowIpdPreview(false)} className="p-2 hover:bg-gray-200 rounded-lg">
                <X size={18} />
              </button>
            </div>

            {/* Email preview */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              <div className="bg-white shadow-lg rounded-lg overflow-hidden mx-auto" style={{ maxWidth: '600px' }}>
                <iframe
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;font-family:sans-serif;}</style></head><body>${ipdPreviewData.html}</body></html>`}
                  className="w-full border-0"
                  style={{ minHeight: '500px' }}
                  title="Podgląd emaila IPD"
                  onLoad={(e) => {
                    const iframe = e.target as HTMLIFrameElement;
                    const doc = iframe.contentDocument;
                    if (doc) iframe.style.height = Math.max(doc.body.scrollHeight + 40, 500) + 'px';
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowIpdPreview(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">
                Zamknij
              </button>
              <button
                onClick={handleIpdSend}
                disabled={ipdSending}
                className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Send size={14} />
                {ipdSending ? 'Wysyłanie...' : 'Wyślij email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// LOAN INFO SECTION (Wsparcie na Starcie / pożyczka)
// ═══════════════════════════════════════════════════════

const LOAN_STATUSES = [
  { value: '', label: 'Nie ustawiony' },
  { value: 'zapytanie', label: 'Zapytanie' },
  { value: 'kompletacja_dok', label: 'Kompletacja dokumentów' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony' },
  { value: 'w_ocenie', label: 'W ocenie' },
  { value: 'zatwierdzony', label: 'Zatwierdźony' },
  { value: 'umowa_podpisana', label: 'Umowa podpisana' },
  { value: 'srodki_wyplacone', label: 'Środki wypłacone' },
  { value: 'w_splacie', label: 'W trakcie spłaty' },
  { value: 'forgiveness_applied', label: 'Wniosek o umorzenie' },
  { value: 'forgiven', label: 'Umorzona' },
  { value: 'zakonczony', label: 'Spłacona' },
  { value: 'odrzućony', label: 'Odrzucony' },
];

const APPLICANT_CATEGORIES = [
  { value: '', label: 'Nie wybrano' },
  { value: 'bezrobotny', label: 'Bezrobotny' },
  { value: 'student', label: 'Student' },
  { value: 'absolwent', label: 'Absolwent' },
  { value: 'opiekun', label: 'Opiekun os. niepełnosprawnej' },
  { value: 'zatrudniony', label: 'Poszukujący pracy (zatrudniony)' },
];

function LoanInfoSection({ application, onSave }: { application: any; onSave: (data: Record<string, any>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [operators, setOperators] = useState<any[]>([]);
  const [form, setForm] = useState({
    loan_amount: application.loan_amount || '',
    loan_operator: application.loan_operator || '',
    loan_status: application.loan_status || '',
    repayment_period: application.repayment_period || '',
    grace_period: application.grace_period || '',
    forgiveness_eligible: application.forgiveness_eligible || false,
    forgiveness_amount: application.forgiveness_amount || '',
    forgiveness_request_date: application.forgiveness_request_date || '',
    forgiveness_decision_date: application.forgiveness_decision_date || '',
    forgiveness_decision: application.forgiveness_decision || '',
    forgiveness_approved_amount: application.forgiveness_approved_amount || '',
    forgiveness_notes: application.forgiveness_notes || '',
    applicant_category: application.applicant_category || '',
    service_type: application.service_type || 'wsparcie_na_starcie',
  });

  React.useEffect(() => {
    crmApi.getLoanOperators().then(res => setOperators(res.operators || [])).catch(err => console.warn('[DetailSidebar] operators fetch:', err.message));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        loan_amount: form.loan_amount ? Number(form.loan_amount) : null,
        loan_operator: form.loan_operator || null,
        loan_status: form.loan_status || null,
        repayment_period: form.repayment_period ? Number(form.repayment_period) : null,
        grace_period: form.grace_period ? Number(form.grace_period) : null,
        forgiveness_eligible: form.forgiveness_eligible,
        forgiveness_amount: form.forgiveness_amount ? Number(form.forgiveness_amount) : null,
        forgiveness_request_date: form.forgiveness_request_date || null,
        forgiveness_decision_date: form.forgiveness_decision_date || null,
        forgiveness_decision: form.forgiveness_decision || null,
        forgiveness_approved_amount: form.forgiveness_approved_amount ? Number(form.forgiveness_approved_amount) : null,
        forgiveness_notes: form.forgiveness_notes || null,
        applicant_category: form.applicant_category || null,
        service_type: form.service_type,
      });
      setEditing(false);
    } catch (e) {
      console.error('Loan save error:', e);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = LOAN_STATUSES.find(s => s.value === (application.loan_status || ''))?.label || application.loan_status || 'Nie ustawiony';
  const categoryLabel = APPLICANT_CATEGORIES.find(c => c.value === (application.applicant_category || ''))?.label || application.applicant_category || 'Nie wybrano';

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg shadow p-6 border border-emerald-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-emerald-800">Dane pożyczki</h3>
        <button
          type="button"
          onClick={() => setEditing(!editing)}
          className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
        >
          {editing ? <><X size={12} className="inline mr-1" />Anuluj</> : <><Edit size={12} className="inline mr-1" />Edytuj</>}
        </button>
      </div>

      {editing ? (
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Typ programu</label>
            <select aria-label="Typ programu" value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} className="w-full border rounded p-2 text-sm">
              {Object.entries(SERVICE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Kwota pożyczki (zł)</label>
            <input type="number" min="0" max="175000" value={form.loan_amount} onChange={e => setForm(f => ({ ...f, loan_amount: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 120000" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Operator pożyczkowy</label>
            <select aria-label="Operator pożyczkowy" value={form.loan_operator} onChange={e => setForm(f => ({ ...f, loan_operator: e.target.value }))} className="w-full border rounded p-2 text-sm">
              <option value="">Nie wybrany</option>
              {operators.map((op: any) => <option key={op.id} value={op.short_name}>{op.name} ({op.short_name})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status pożyczki</label>
            <select aria-label="Status pożyczki" value={form.loan_status} onChange={e => setForm(f => ({ ...f, loan_status: e.target.value }))} className="w-full border rounded p-2 text-sm">
              {LOAN_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Okres spłaty (lat)</label>
              <input type="number" min="1" max="7" value={form.repayment_period} onChange={e => setForm(f => ({ ...f, repayment_period: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="1-7" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Karencja (msc)</label>
              <input type="number" min="0" max="12" value={form.grace_period} onChange={e => setForm(f => ({ ...f, grace_period: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="0-12" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Kategoria wnioskodawcy</label>
            <select aria-label="Kategoria wnioskodawcy" value={form.applicant_category} onChange={e => setForm(f => ({ ...f, applicant_category: e.target.value }))} className="w-full border rounded p-2 text-sm">
              {APPLICANT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.forgiveness_eligible} onChange={e => setForm(f => ({ ...f, forgiveness_eligible: e.target.checked }))} className="rounded" />
              Kwalifikuje się do umorzenia
            </label>
          </div>
          {form.forgiveness_eligible && (
            <div className="space-y-3 bg-green-50 rounded-lg p-3 border border-green-200">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Kwota wnioskowana (zł)</label>
                <input type="number" min="0" max="52500" value={form.forgiveness_amount} onChange={e => setForm(f => ({ ...f, forgiveness_amount: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 52500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Data złożenia wniosku</label>
                <input type="date" value={form.forgiveness_request_date} onChange={e => setForm(f => ({ ...f, forgiveness_request_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Decyzja</label>
                <select value={form.forgiveness_decision} onChange={e => setForm(f => ({ ...f, forgiveness_decision: e.target.value }))} className="w-full border rounded p-2 text-sm">
                  <option value="">Oczekuje</option>
                  <option value="approved">Zatwierdźone</option>
                  <option value="partial">Częściowe</option>
                  <option value="rejected">Odrzucone</option>
                </select>
              </div>
              {(form.forgiveness_decision === 'approved' || form.forgiveness_decision === 'partial') && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Kwota przyznana (zł)</label>
                  <input type="number" min="0" max="52500" value={form.forgiveness_approved_amount} onChange={e => setForm(f => ({ ...f, forgiveness_approved_amount: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Kwota przyznana" />
                </div>
              )}
              {form.forgiveness_decision && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Data decyzji</label>
                  <input type="date" value={form.forgiveness_decision_date} onChange={e => setForm(f => ({ ...f, forgiveness_decision_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notatki umorzenia</label>
                <textarea value={form.forgiveness_notes} onChange={e => setForm(f => ({ ...f, forgiveness_notes: e.target.value }))} className="w-full border rounded p-2 text-sm" rows={2} placeholder="Uwagi dot. umorzenia..." />
              </div>
            </div>
          )}
          <button type="button" onClick={handleSave} disabled={saving} className="w-full bg-emerald-600 text-white py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center text-sm">
            <Save className="mr-2" size={14} />
            {saving ? 'Zapisywanie...' : 'Zapisz dane pożyczki'}
          </button>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Kwota pożyczki</span>
            <span className="font-bold text-emerald-700">{application.loan_amount ? `${Number(application.loan_amount).toLocaleString('pl-PL')} zł` : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Operator</span>
            <span className="font-medium">{application.loan_operator || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Status</span>
            <span className="font-medium">{statusLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Spłata</span>
            <span>{application.repayment_period ? `${application.repayment_period} lat` : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Karencja</span>
            <span>{application.grace_period ? `${application.grace_period} msc` : '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Kategoria</span>
            <span>{categoryLabel}</span>
          </div>
          {application.forgiveness_eligible && (
            <div className="bg-green-50 rounded-lg p-3 space-y-1.5 border border-green-200">
              <div className="flex justify-between">
                <span className="text-gray-600">Umorzenie (wnioskowane)</span>
                <span className="font-bold text-green-600">{application.forgiveness_amount ? `${Number(application.forgiveness_amount).toLocaleString('pl-PL')} zł` : 'Tak'}</span>
              </div>
              {application.forgiveness_request_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Data wniosku</span>
                  <span>{new Date(application.forgiveness_request_date).toLocaleDateString('pl-PL')}</span>
                </div>
              )}
              {application.forgiveness_decision && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Decyzja</span>
                  <span className={`font-medium ${application.forgiveness_decision === 'approved' ? 'text-green-600' : application.forgiveness_decision === 'rejected' ? 'text-red-600' : 'text-amber-600'}`}>
                    {application.forgiveness_decision === 'approved' ? 'Zatwierdźone' : application.forgiveness_decision === 'partial' ? 'Częściowe' : application.forgiveness_decision === 'rejected' ? 'Odrzucone' : 'Oczekuje'}
                  </span>
                </div>
              )}
              {application.forgiveness_approved_amount && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Kwota przyznana</span>
                  <span className="font-bold text-green-700">{Number(application.forgiveness_approved_amount).toLocaleString('pl-PL')} zł</span>
                </div>
              )}
              {application.forgiveness_decision_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Data decyzji</span>
                  <span>{new Date(application.forgiveness_decision_date).toLocaleDateString('pl-PL')}</span>
                </div>
              )}
              {application.forgiveness_notes && (
                <div className="mt-1 text-xs text-gray-500 italic">{application.forgiveness_notes}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// KFS INFO SECTION (Krajowy Fundusz Szkoleniowy)
// ═══════════════════════════════════════════════════════

const KFS_STATUS_OPTIONS = [
  { value: '', label: 'Nie ustawiony' },
  { value: 'zapytanie', label: 'Zapytanie' },
  { value: 'analiza', label: 'Analiza potrzeb' },
  { value: 'application_prep', label: 'Przygotowanie wniosku' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony' },
  { value: 'zatwierdzony', label: 'Zatwierdźony' },
  { value: 'szkolenie_w_toku', label: 'Szkolenie w toku' },
  { value: 'zakonczony', label: 'Zakończone' },
  { value: 'odrzućony', label: 'Anulowane' },
];

function KfsInfoSection({ application, onSave }: { application: any; onSave: (data: Record<string, any>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kfs_company_name: application.kfs_company_name || '',
    kfs_company_nip: application.kfs_company_nip || '',
    kfs_company_regon: application.kfs_company_regon || '',
    kfs_company_address: application.kfs_company_address || '',
    kfs_company_size: application.kfs_company_size || '',
    kfs_employee_count: application.kfs_employee_count || '',
    kfs_contact_position: application.kfs_contact_position || '',
    kfs_training_area: application.kfs_training_area || '',
    kfs_training_description: application.kfs_training_description || '',
    kfs_participant_count: application.kfs_participant_count || '',
    kfs_estimated_budget: application.kfs_estimated_budget || '',
    kfs_preferred_dates: application.kfs_preferred_dates || '',
    kfs_expected_outcomes: application.kfs_expected_outcomes || '',
    kfs_status: application.kfs_status || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        kfs_company_name: form.kfs_company_name || null,
        kfs_company_nip: form.kfs_company_nip || null,
        kfs_company_regon: form.kfs_company_regon || null,
        kfs_company_address: form.kfs_company_address || null,
        kfs_company_size: form.kfs_company_size || null,
        kfs_employee_count: form.kfs_employee_count ? Number(form.kfs_employee_count) : null,
        kfs_contact_position: form.kfs_contact_position || null,
        kfs_training_area: form.kfs_training_area || null,
        kfs_training_description: form.kfs_training_description || null,
        kfs_participant_count: form.kfs_participant_count ? Number(form.kfs_participant_count) : null,
        kfs_estimated_budget: form.kfs_estimated_budget ? Number(form.kfs_estimated_budget) : null,
        kfs_preferred_dates: form.kfs_preferred_dates || null,
        kfs_expected_outcomes: form.kfs_expected_outcomes || null,
        kfs_status: form.kfs_status || null,
      });
      setEditing(false);
    } catch (e) {
      console.error('KFS save error:', e);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = KFS_STATUS_OPTIONS.find(s => s.value === (application.kfs_status || ''))?.label || application.kfs_status || 'Nie ustawiony';

  return (
    <>
      {/* KFS Company */}
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg shadow p-6 border border-amber-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-amber-800">Firma (KFS)</h3>
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
          >
            {editing ? <><X size={12} className="inline mr-1" />Anuluj</> : <><Edit size={12} className="inline mr-1" />Edytuj</>}
          </button>
        </div>

        {editing ? (
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nazwa firmy</label>
              <input type="text" value={form.kfs_company_name} onChange={e => setForm(f => ({ ...f, kfs_company_name: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Nazwa firmy" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">NIP</label>
                <input type="text" value={form.kfs_company_nip} onChange={e => setForm(f => ({ ...f, kfs_company_nip: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="NIP" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">REGON</label>
                <input type="text" value={form.kfs_company_regon} onChange={e => setForm(f => ({ ...f, kfs_company_regon: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="REGON" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Adres firmy</label>
              <input type="text" value={form.kfs_company_address} onChange={e => setForm(f => ({ ...f, kfs_company_address: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Adres firmy" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Wielkość firmy</label>
                <select aria-label="Wielkość firmy" value={form.kfs_company_size} onChange={e => setForm(f => ({ ...f, kfs_company_size: e.target.value }))} className="w-full border rounded p-2 text-sm">
                  <option value="">Nie wybrano</option>
                  <option value="micro">Mikro (do 10)</option>
                  <option value="small">Mała (11-50)</option>
                  <option value="medium">Średnia (51-250)</option>
                  <option value="large">Duża (250+)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Liczba pracowników</label>
                <input type="number" min="0" value={form.kfs_employee_count} onChange={e => setForm(f => ({ ...f, kfs_employee_count: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 25" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Stanowisko osoby kontaktowej</label>
              <input type="text" value={form.kfs_contact_position} onChange={e => setForm(f => ({ ...f, kfs_contact_position: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. Kierownik HR" />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Firma</span><span className="font-medium">{application.kfs_company_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">NIP</span><span className="font-medium">{application.kfs_company_nip || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">REGON</span><span className="font-medium">{application.kfs_company_regon || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Adres</span><span className="font-medium">{application.kfs_company_address || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Wielkość</span><span className="font-medium">{application.kfs_company_size || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Pracownicy</span><span className="font-medium">{application.kfs_employee_count || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Stanowisko kontaktu</span><span className="font-medium">{application.kfs_contact_position || '—'}</span></div>
          </div>
        )}
      </div>

      {/* KFS Training */}
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg shadow p-6 border border-amber-200">
        <h3 className="font-bold text-amber-800 mb-4">Szkolenie (KFS)</h3>
        {editing ? (
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Obszar szkoleniowy</label>
              <input type="text" value={form.kfs_training_area} onChange={e => setForm(f => ({ ...f, kfs_training_area: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. IT, zarządzanie, marketing" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Opis szkolenia</label>
              <textarea value={form.kfs_training_description} onChange={e => setForm(f => ({ ...f, kfs_training_description: e.target.value }))} rows={3} className="w-full border rounded p-2 text-sm" placeholder="Szczegółowy opis planowanego szkolenia" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Liczba uczestników</label>
                <input type="number" min="1" value={form.kfs_participant_count} onChange={e => setForm(f => ({ ...f, kfs_participant_count: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 5" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Szacowany budżet (zł)</label>
                <input type="number" min="0" value={form.kfs_estimated_budget} onChange={e => setForm(f => ({ ...f, kfs_estimated_budget: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 15000" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Preferowane terminy</label>
              <input type="text" value={form.kfs_preferred_dates} onChange={e => setForm(f => ({ ...f, kfs_preferred_dates: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. kwiecień-maj 2026" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Oczekiwane rezultaty</label>
              <textarea value={form.kfs_expected_outcomes} onChange={e => setForm(f => ({ ...f, kfs_expected_outcomes: e.target.value }))} rows={2} className="w-full border rounded p-2 text-sm" placeholder="Oczekiwane efekty szkolenia" />
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Obszar</span><span className="font-medium">{application.kfs_training_area || '—'}</span></div>
            {application.kfs_training_description && (
              <div><span className="text-gray-600 block mb-1">Opis</span><p className="text-gray-800 text-xs bg-gray-50 rounded p-2">{application.kfs_training_description}</p></div>
            )}
            <div className="flex justify-between"><span className="text-gray-600">Uczestnicy</span><span className="font-medium">{application.kfs_participant_count || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Budżet</span><span className="font-bold text-amber-700">{application.kfs_estimated_budget ? `${Number(application.kfs_estimated_budget).toLocaleString('pl-PL')} zł` : '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Terminy</span><span className="font-medium">{application.kfs_preferred_dates || '—'}</span></div>
            {application.kfs_expected_outcomes && (
              <div><span className="text-gray-600 block mb-1">Rezultaty</span><p className="text-gray-800 text-xs bg-gray-50 rounded p-2">{application.kfs_expected_outcomes}</p></div>
            )}
          </div>
        )}
      </div>

      {/* KFS Status */}
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg shadow p-6 border border-amber-200">
        <h3 className="font-bold text-amber-800 mb-4">Status KFS</h3>
        {editing ? (
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status KFS</label>
              <select aria-label="Status KFS" value={form.kfs_status} onChange={e => setForm(f => ({ ...f, kfs_status: e.target.value }))} className="w-full border rounded p-2 text-sm">
                {KFS_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={handleSave} disabled={saving} className="w-full bg-amber-600 text-white py-2 rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center text-sm">
              <Save className="mr-2" size={14} />
              {saving ? 'Zapisywanie...' : 'Zapisz dane KFS'}
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status</span>
              <span className="font-medium">{statusLabel}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// ZUS INFO SECTION (Ulgi ZUS)
// ═══════════════════════════════════════════════════════

const ZUS_STATUS_OPTIONS = [
  { value: '', label: 'Nie ustawiony' },
  { value: 'zapytanie', label: 'Zapytanie' },
  { value: 'analiza', label: 'Analiza' },
  { value: 'documents', label: 'Kompletacja dokumentów' },
  { value: 'wniosek_zlozony', label: 'Wniosek złożony' },
  { value: 'w_rozpatrywaniu', label: 'W rozpatrywaniu' },
  { value: 'decision', label: 'Decyzja' },
  { value: 'installments', label: 'Układ ratalny' },
  { value: 'zakonczony', label: 'Zakończone' },
  { value: 'anulowany', label: 'Anulowane' },
];

function ZusInfoSection({ application, onSave }: { application: any; onSave: (data: Record<string, any>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    zus_company_name: application.zus_company_name || '',
    zus_company_nip: application.zus_company_nip || '',
    zus_debt_type: application.zus_debt_type || '',
    zus_debt_amount: application.zus_debt_amount || '',
    zus_debt_period: application.zus_debt_period || '',
    zus_business_status: application.zus_business_status || '',
    zus_requested_solution: application.zus_requested_solution || '',
    zus_financial_situation: application.zus_financial_situation || '',
    zus_monthly_income: application.zus_monthly_income || '',
    zus_monthly_expenses: application.zus_monthly_expenses || '',
    zus_status: application.zus_status || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        zus_company_name: form.zus_company_name || null,
        zus_company_nip: form.zus_company_nip || null,
        zus_debt_type: form.zus_debt_type || null,
        zus_debt_amount: form.zus_debt_amount ? Number(form.zus_debt_amount) : null,
        zus_debt_period: form.zus_debt_period || null,
        zus_business_status: form.zus_business_status || null,
        zus_requested_solution: form.zus_requested_solution || null,
        zus_financial_situation: form.zus_financial_situation || null,
        zus_monthly_income: form.zus_monthly_income ? Number(form.zus_monthly_income) : null,
        zus_monthly_expenses: form.zus_monthly_expenses ? Number(form.zus_monthly_expenses) : null,
        zus_status: form.zus_status || null,
      });
      setEditing(false);
    } catch (e) {
      console.error('ZUS save error:', e);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = ZUS_STATUS_OPTIONS.find(s => s.value === (application.zus_status || ''))?.label || application.zus_status || 'Nie ustawiony';

  return (
    <>
      {/* ZUS Company & Debt */}
      <div className="bg-gradient-to-br from-slate-50 to-white rounded-lg shadow p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">Dane ZUS</h3>
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
          >
            {editing ? <><X size={12} className="inline mr-1" />Anuluj</> : <><Edit size={12} className="inline mr-1" />Edytuj</>}
          </button>
        </div>

        {editing ? (
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nazwa firmy</label>
              <input type="text" value={form.zus_company_name} onChange={e => setForm(f => ({ ...f, zus_company_name: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Nazwa firmy" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">NIP</label>
              <input type="text" value={form.zus_company_nip} onChange={e => setForm(f => ({ ...f, zus_company_nip: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="NIP" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Rodzaj zadłużenia</label>
              <input type="text" value={form.zus_debt_type} onChange={e => setForm(f => ({ ...f, zus_debt_type: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. składki społeczne, zdrowotne" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Kwota zadłużenia (zł)</label>
                <input type="number" min="0" value={form.zus_debt_amount} onChange={e => setForm(f => ({ ...f, zus_debt_amount: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 25000" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Okres zadłużenia</label>
                <input type="text" value={form.zus_debt_period} onChange={e => setForm(f => ({ ...f, zus_debt_period: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 2023-2025" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status działalności</label>
              <input type="text" value={form.zus_business_status} onChange={e => setForm(f => ({ ...f, zus_business_status: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. aktywna, zawieszona" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Wnioskowane rozwiązanie</label>
              <input type="text" value={form.zus_requested_solution} onChange={e => setForm(f => ({ ...f, zus_requested_solution: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. układ ratalny, umorzenie" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Sytuacja finansowa</label>
              <textarea value={form.zus_financial_situation} onChange={e => setForm(f => ({ ...f, zus_financial_situation: e.target.value }))} rows={2} className="w-full border rounded p-2 text-sm" placeholder="Opis sytuacji finansowej" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Dochód miesięczny (zł)</label>
                <input type="number" min="0" value={form.zus_monthly_income} onChange={e => setForm(f => ({ ...f, zus_monthly_income: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 8000" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Wydatki miesięczne (zł)</label>
                <input type="number" min="0" value={form.zus_monthly_expenses} onChange={e => setForm(f => ({ ...f, zus_monthly_expenses: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 5000" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status sprawy ZUS</label>
              <select aria-label="Status sprawy ZUS" value={form.zus_status} onChange={e => setForm(f => ({ ...f, zus_status: e.target.value }))} className="w-full border rounded p-2 text-sm">
                {ZUS_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={handleSave} disabled={saving} className="w-full bg-slate-600 text-white py-2 rounded-lg font-medium hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center text-sm">
              <Save className="mr-2" size={14} />
              {saving ? 'Zapisywanie...' : 'Zapisz dane ZUS'}
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Firma</span><span className="font-medium">{application.zus_company_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">NIP</span><span className="font-medium">{application.zus_company_nip || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Rodzaj długu</span><span className="font-medium">{application.zus_debt_type || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Kwota długu</span><span className="font-bold text-red-600">{application.zus_debt_amount ? `${Number(application.zus_debt_amount).toLocaleString('pl-PL')} zł` : '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Okres</span><span className="font-medium">{application.zus_debt_period || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Status działalności</span><span className="font-medium">{application.zus_business_status || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Rozwiązanie</span><span className="font-medium">{application.zus_requested_solution || '—'}</span></div>
            {application.zus_financial_situation && (
              <div><span className="text-gray-600 block mb-1">Sytuacja finansowa</span><p className="text-gray-800 text-xs bg-gray-50 rounded p-2">{application.zus_financial_situation}</p></div>
            )}
            <div className="flex justify-between"><span className="text-gray-600">Dochód msc.</span><span className="font-medium">{application.zus_monthly_income ? `${Number(application.zus_monthly_income).toLocaleString('pl-PL')} zł` : '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Wydatki msc.</span><span className="font-medium">{application.zus_monthly_expenses ? `${Number(application.zus_monthly_expenses).toLocaleString('pl-PL')} zł` : '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Status</span><span className="font-medium">{statusLabel}</span></div>
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// BHP INFO SECTION (Szkolenia BHP)
// ═══════════════════════════════════════════════════════

const BHP_STATUS_OPTIONS = [
  { value: '', label: 'Nie ustawiony' },
  { value: 'zapytanie', label: 'Zapytanie' },
  { value: 'zaplanowany', label: 'Zaplanowane' },
  { value: 'w_toku', label: 'Szkolenie w toku' },
  { value: 'certificates', label: 'Wydawanie certyfikatów' },
  { value: 'zakonczony', label: 'Zakończone' },
  { value: 'anulowany', label: 'Anulowane' },
];

const BHP_TRAINING_TYPES = [
  { value: '', label: 'Nie wybrano' },
  { value: 'wstepne', label: 'Wstępne' },
  { value: 'okresowe', label: 'Okresowe' },
  { value: 'kierownicze', label: 'Kierownicze' },
  { value: 'instruktorskie', label: 'Instruktorskie' },
];

function BhpInfoSection({ application, onSave }: { application: any; onSave: (data: Record<string, any>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    bhp_company_name: application.bhp_company_name || '',
    bhp_company_nip: application.bhp_company_nip || '',
    bhp_company_address: application.bhp_company_address || '',
    bhp_company_industry: application.bhp_company_industry || '',
    bhp_training_type: application.bhp_training_type || '',
    bhp_participant_count: application.bhp_participant_count || '',
    bhp_training_location: application.bhp_training_location || '',
    bhp_preferred_date: application.bhp_preferred_date || '',
    bhp_scheduled_date: application.bhp_scheduled_date || '',
    bhp_trainer_assigned: application.bhp_trainer_assigned || '',
    bhp_contact_position: application.bhp_contact_position || '',
    bhp_status: application.bhp_status || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        bhp_company_name: form.bhp_company_name || null,
        bhp_company_nip: form.bhp_company_nip || null,
        bhp_company_address: form.bhp_company_address || null,
        bhp_company_industry: form.bhp_company_industry || null,
        bhp_training_type: form.bhp_training_type || null,
        bhp_participant_count: form.bhp_participant_count ? Number(form.bhp_participant_count) : null,
        bhp_training_location: form.bhp_training_location || null,
        bhp_preferred_date: form.bhp_preferred_date || null,
        bhp_scheduled_date: form.bhp_scheduled_date || null,
        bhp_trainer_assigned: form.bhp_trainer_assigned || null,
        bhp_contact_position: form.bhp_contact_position || null,
        bhp_status: form.bhp_status || null,
      });
      setEditing(false);
    } catch (e) {
      console.error('BHP save error:', e);
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = BHP_STATUS_OPTIONS.find(s => s.value === (application.bhp_status || ''))?.label || application.bhp_status || 'Nie ustawiony';
  const trainingTypeLabel = BHP_TRAINING_TYPES.find(t => t.value === (application.bhp_training_type || ''))?.label || application.bhp_training_type || '—';

  return (
    <>
      <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg shadow p-6 border border-orange-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-orange-800">Dane BHP</h3>
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
          >
            {editing ? <><X size={12} className="inline mr-1" />Anuluj</> : <><Edit size={12} className="inline mr-1" />Edytuj</>}
          </button>
        </div>

        {editing ? (
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nazwa firmy</label>
              <input type="text" value={form.bhp_company_name} onChange={e => setForm(f => ({ ...f, bhp_company_name: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Nazwa firmy" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">NIP</label>
                <input type="text" value={form.bhp_company_nip} onChange={e => setForm(f => ({ ...f, bhp_company_nip: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="NIP" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Branża</label>
                <input type="text" value={form.bhp_company_industry} onChange={e => setForm(f => ({ ...f, bhp_company_industry: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. budownictwo" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Adres firmy</label>
              <input type="text" value={form.bhp_company_address} onChange={e => setForm(f => ({ ...f, bhp_company_address: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Adres firmy" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Rodzaj szkolenia</label>
                <select aria-label="Rodzaj szkolenia BHP" value={form.bhp_training_type} onChange={e => setForm(f => ({ ...f, bhp_training_type: e.target.value }))} className="w-full border rounded p-2 text-sm">
                  {BHP_TRAINING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Liczba uczestników</label>
                <input type="number" min="1" value={form.bhp_participant_count} onChange={e => setForm(f => ({ ...f, bhp_participant_count: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 10" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Miejsce szkolenia</label>
              <input type="text" value={form.bhp_training_location} onChange={e => setForm(f => ({ ...f, bhp_training_location: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. siedziba firmy, online" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Preferowany termin</label>
                <input type="date" aria-label="Preferowany termin" value={form.bhp_preferred_date} onChange={e => setForm(f => ({ ...f, bhp_preferred_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Zaplanowana data</label>
                <input type="date" aria-label="Zaplanowana data" value={form.bhp_scheduled_date} onChange={e => setForm(f => ({ ...f, bhp_scheduled_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Przypisany szkoleniowiec</label>
              <input type="text" value={form.bhp_trainer_assigned} onChange={e => setForm(f => ({ ...f, bhp_trainer_assigned: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. Jan Kowalski" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Stanowisko kontaktu</label>
              <input type="text" value={form.bhp_contact_position} onChange={e => setForm(f => ({ ...f, bhp_contact_position: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. Kierownik BHP" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status BHP</label>
              <select aria-label="Status BHP" value={form.bhp_status} onChange={e => setForm(f => ({ ...f, bhp_status: e.target.value }))} className="w-full border rounded p-2 text-sm">
                {BHP_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <button type="button" onClick={handleSave} disabled={saving} className="w-full bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center text-sm">
              <Save className="mr-2" size={14} />
              {saving ? 'Zapisywanie...' : 'Zapisz dane BHP'}
            </button>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Firma</span><span className="font-medium">{application.bhp_company_name || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">NIP</span><span className="font-medium">{application.bhp_company_nip || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Adres</span><span className="font-medium">{application.bhp_company_address || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Branża</span><span className="font-medium">{application.bhp_company_industry || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Rodzaj szkolenia</span><span className="font-medium">{trainingTypeLabel}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Uczestnicy</span><span className="font-medium">{application.bhp_participant_count || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Miejsce</span><span className="font-medium">{application.bhp_training_location || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Pref. termin</span><span className="font-medium">{application.bhp_preferred_date || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Zaplanowano</span><span className="font-medium">{application.bhp_scheduled_date || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Szkoleniowiec</span><span className="font-medium">{application.bhp_trainer_assigned || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Kontakt</span><span className="font-medium">{application.bhp_contact_position || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Status</span><span className="font-medium">{statusLabel}</span></div>
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════
// DEV LOAN INFO SECTION (Pożyczki Rozwojowe)
// ═══════════════════════════════════════════════════════

function DevLoanInfoSection({ application, onSave }: { application: any; onSave: (data: Record<string, any>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dev_loan_business_name: application.dev_loan_business_name || '',
    dev_loan_business_nip: application.dev_loan_business_nip || '',
    dev_loan_revenue: application.dev_loan_revenue || '',
    dev_loan_employees: application.dev_loan_employees || '',
    dev_loan_years_operating: application.dev_loan_years_operating || '',
    dev_loan_investment_purpose: application.dev_loan_investment_purpose || '',
    dev_loan_development_plan: application.dev_loan_development_plan || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        dev_loan_business_name: form.dev_loan_business_name || null,
        dev_loan_business_nip: form.dev_loan_business_nip || null,
        dev_loan_revenue: form.dev_loan_revenue ? Number(form.dev_loan_revenue) : null,
        dev_loan_employees: form.dev_loan_employees ? Number(form.dev_loan_employees) : null,
        dev_loan_years_operating: form.dev_loan_years_operating ? Number(form.dev_loan_years_operating) : null,
        dev_loan_investment_purpose: form.dev_loan_investment_purpose || null,
        dev_loan_development_plan: form.dev_loan_development_plan || null,
      });
      setEditing(false);
    } catch (e) {
      console.error('Dev loan save error:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-cyan-50 to-white rounded-lg shadow p-6 border border-cyan-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-cyan-800">Dane Pożyczki Rozwojowej</h3>
        <button
          type="button"
          onClick={() => setEditing(!editing)}
          className="text-xs px-2 py-1 rounded bg-cyan-100 text-cyan-700 hover:bg-cyan-200 transition-colors"
        >
          {editing ? <><X size={12} className="inline mr-1" />Anuluj</> : <><Edit size={12} className="inline mr-1" />Edytuj</>}
        </button>
      </div>

      {editing ? (
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Nazwa firmy</label>
            <input type="text" value={form.dev_loan_business_name} onChange={e => setForm(f => ({ ...f, dev_loan_business_name: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="Nazwa firmy" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">NIP</label>
            <input type="text" value={form.dev_loan_business_nip} onChange={e => setForm(f => ({ ...f, dev_loan_business_nip: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="NIP" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Przychód roczny (zł)</label>
              <input type="number" min="0" value={form.dev_loan_revenue} onChange={e => setForm(f => ({ ...f, dev_loan_revenue: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 500000" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Pracownicy</label>
              <input type="number" min="0" value={form.dev_loan_employees} onChange={e => setForm(f => ({ ...f, dev_loan_employees: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 12" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Lat działalności</label>
              <input type="number" min="0" value={form.dev_loan_years_operating} onChange={e => setForm(f => ({ ...f, dev_loan_years_operating: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="np. 5" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Cel inwestycji</label>
            <textarea value={form.dev_loan_investment_purpose} onChange={e => setForm(f => ({ ...f, dev_loan_investment_purpose: e.target.value }))} rows={2} className="w-full border rounded p-2 text-sm" placeholder="Na co zostanie przeznaczona pożyczka" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Plan rozwoju</label>
            <textarea value={form.dev_loan_development_plan} onChange={e => setForm(f => ({ ...f, dev_loan_development_plan: e.target.value }))} rows={3} className="w-full border rounded p-2 text-sm" placeholder="Opis planu rozwoju firmy" />
          </div>
          <button type="button" onClick={handleSave} disabled={saving} className="w-full bg-cyan-600 text-white py-2 rounded-lg font-medium hover:bg-cyan-700 disabled:opacity-50 flex items-center justify-center text-sm">
            <Save className="mr-2" size={14} />
            {saving ? 'Zapisywanie...' : 'Zapisz dane pożyczki rozwojowej'}
          </button>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-600">Firma</span><span className="font-medium">{application.dev_loan_business_name || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">NIP</span><span className="font-medium">{application.dev_loan_business_nip || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Przychód roczny</span><span className="font-bold text-cyan-700">{application.dev_loan_revenue ? `${Number(application.dev_loan_revenue).toLocaleString('pl-PL')} zł` : '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Pracownicy</span><span className="font-medium">{application.dev_loan_employees || '—'}</span></div>
          <div className="flex justify-between"><span className="text-gray-600">Lat działalności</span><span className="font-medium">{application.dev_loan_years_operating || '—'}</span></div>
          {application.dev_loan_investment_purpose && (
            <div><span className="text-gray-600 block mb-1">Cel inwestycji</span><p className="text-gray-800 text-xs bg-gray-50 rounded p-2">{application.dev_loan_investment_purpose}</p></div>
          )}
          {application.dev_loan_development_plan && (
            <div><span className="text-gray-600 block mb-1">Plan rozwoju</span><p className="text-gray-800 text-xs bg-gray-50 rounded p-2">{application.dev_loan_development_plan}</p></div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Conditions Confirmation Section ──────────────────────────────────
function ConditionsSection({ application, onUpdate }: { application: any; onUpdate: () => void }) {
  const [notes, setNotes] = useState(application.conditions_notes || '');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async (confirmed: boolean) => {
    setSaving(true);
    try {
      await authAxios.patch(`/api/crm/applications/${application.id}/conditions`, { confirmed, notes });
      onUpdate();
    } catch (err) {
      console.error('Conditions update failed:', err);
    }
    setSaving(false);
  };

  const status = application.conditions_confirmed;

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-brand" />
        Potwierdźenie warunków
      </h4>
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
        status === true ? 'bg-green-100 text-green-700' :
        status === false ? 'bg-red-100 text-red-700' :
        'bg-gray-100 text-gray-500'
      }`}>
        {status === true ? <CheckCircle className="w-4 h-4" /> :
         status === false ? <XCircle className="w-4 h-4" /> :
         <Clock className="w-4 h-4" />}
        {status === true ? 'Warunki potwierdzone' :
         status === false ? 'Warunki odrzucone' : 'Oczekuje na potwierdźenie'}
      </div>
      {application.conditions_confirmed_at && (
        <p className="text-xs text-gray-500">
          Data: {new Date(application.conditions_confirmed_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Notatki operatora (zmiany warunków, uwagi)..."
        rows={2}
        className="w-full text-xs border rounded-lg px-3 py-2 focus:ring-brand focus:border-brand"
      />
      <div className="flex gap-2">
        <button
          onClick={() => handleConfirm(true)}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          <CheckCircle className="w-3 h-3" /> Potwierdzone
        </button>
        <button
          onClick={() => handleConfirm(false)}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          <XCircle className="w-3 h-3" /> Odrzucone
        </button>
      </div>
    </div>
  );
}

// ── Print Flags Section ──────────────────────────────────────────────
function PrintFlagsSection({ application, onUpdate }: { application: any; onUpdate: () => void }) {
  const [saving, setSaving] = useState(false);

  const toggle = async (field: 'attachments_print_ready' | 'application_print_ready', value: boolean) => {
    setSaving(true);
    try {
      await authAxios.patch(`/api/crm/applications/${application.id}/print-flags`, { [field]: value });
      onUpdate();
    } catch (err) {
      console.error('Print flag update failed:', err);
    }
    setSaving(false);
  };

  const attReady = application.attachments_print_ready;
  const appReady = application.application_print_ready;
  const anyReady = attReady || appReady;

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
        <FileText className="w-4 h-4 text-brand" />
        Kontrola druku
      </h4>
      <div className={`px-3 py-2 rounded-lg text-xs font-bold text-center ${
        anyReady ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {!anyReady ? 'NIE DRUKOWAĆ' :
         attReady && appReady ? 'DRUKOWAĆ WSZYSTKO' :
         attReady ? 'DRUKOWAĆ ZAŁĄCZNIKI' : 'DRUKOWAĆ WNIOSEK'}
      </div>
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-xs text-gray-700">Załączniki do druku</span>
        <button
          onClick={() => toggle('attachments_print_ready', !attReady)}
          disabled={saving}
          className={`relative w-10 h-5 rounded-full transition-colors ${attReady ? 'bg-green-500' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${attReady ? 'translate-x-5' : ''}`} />
        </button>
      </label>
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-xs text-gray-700">Wniosek do druku</span>
        <button
          onClick={() => toggle('application_print_ready', !appReady)}
          disabled={saving}
          className={`relative w-10 h-5 rounded-full transition-colors ${appReady ? 'bg-green-500' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${appReady ? 'translate-x-5' : ''}`} />
        </button>
      </label>
    </div>
  );
}
